/**
 * Forecast — Monats-Forecasts mit Tagesansicht, Wizard und AI-Vorschlaegen.
 *
 * Features:
 * - Grid-Ansicht: Tages-SOLL/IST pro Kunde mit Segment-Farben
 * - Forecast-Wizard: Monatspakete erfassen, Wochentags-Gewichtung, Verteilung
 * - AI-Vorschlaege fuer automatische Forecast-Generierung
 * - FTE-Bedarfsberechnung, Ramp-Up-Kunden, Live-Daten
 *
 * @page /forecast
 * @roles admin, manager
 * @api /api/forecast/grid, /api/forecast/monats, /api/forecast/segment-summen
 * @api /api/forecast/fte-berechnung, /api/forecast/heute-live, /api/forecast/rampup-customers
 * @api /api/forecast/ai-suggestion, /api/forecast/ai-apply, /api/forecast/ai-suggest/:id
 * @api /api/forecast/kunden-parameter, /api/forecast/weekend-activity
 * @api /api/kunden, /api/feiertage, /api/wochentags-gewichtung, /api/standorte
 * @i18n forecast
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Zap, ChevronLeft, ChevronRight, Plus, Package, Users, Settings, MoreHorizontal } from 'lucide-react';
import ModulEinstellungen from '../components/ModulEinstellungen';
import { Modal, PageHeader, Button, Badge, KpiCard } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

// ─── Helpers ───────────────────────────────────────────────────────

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.message || `HTTP ${r.status}`);
    return data;
  });
}

const segColors = { A: '#338edc', B: '#4D9CE0', C: '#F59E0B' };

function fmt(n) {
  if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}

function getKW(date) {
  const d = new Date(date.getTime());
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

function ratioColor(ratio) {
  if (ratio >= 0.9) return '#34d399';
  if (ratio >= 0.7) return '#eab308';
  return '#ef4444';
}

function lateColor(pct) {
  if (pct <= 3) return '#10B981';
  if (pct <= 7) return '#F59E0B';
  return '#EF4444';
}

/** Parse a datum value from the API into YYYY-MM-DD, handling timezone-shifted ISO strings (CET→UTC). */
function parseDatum(datum) {
  if (!datum) return null;
  // Handle Date objects directly
  if (datum instanceof Date) {
    return datum.toLocaleDateString('sv-SE', { timeZone: 'Europe/Vienna' });
  }
  const s = typeof datum === 'string' ? datum : '';
  // Already a clean YYYY-MM-DD string
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // ISO string with timezone info — parse and convert to Vienna local date
  if (s.length > 10) return new Date(s).toLocaleDateString('sv-SE', { timeZone: 'Europe/Vienna' });
  return s.slice(0, 10);
}

// ─── Confidence Badge ────────────────────────────────────────────────

const confidenceStyles = {
  high:   { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', labelKey: 'forecast.confidence.high' },
  medium: { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/30',   labelKey: 'forecast.confidence.medium' },
  low:    { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/30',     labelKey: 'forecast.confidence.low' },
  none:   { bg: 'bg-gray-500/15',    text: 'text-[var(--text-tertiary)]', border: 'border-gray-500/30',    labelKey: 'forecast.confidence.none' },
};

function ConfidenceBadge({ level, t }) {
  const s = confidenceStyles[level] || confidenceStyles.none;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${s.bg} ${s.text} ${s.border}`}>
      {t(s.labelKey)}
    </span>
  );
}

// ─── Detail Popup ───────────────────────────────────────────────────

function DeltaBadge({ value }) {
  if (value === null || value === undefined) return <span className="text-[var(--text-tertiary)]">—</span>;
  const isNeutral = Math.abs(value) < 5;
  const isPositive = value > 0;
  let bg, fg;
  if (isNeutral) { bg = 'var(--bg-inset,#F8F9FC)'; fg = 'var(--text-secondary)'; }
  else if (isPositive) { bg = 'rgba(239,68,68,0.08)'; fg = '#DC2626'; }
  else { bg = 'rgba(16,185,129,0.08)'; fg = '#059669'; }
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: bg, color: fg }}>
      {isPositive ? '+' : ''}{Math.round(value)}%
    </span>
  );
}

function DetailPopup({ kunde, colLabel, soll, erledigt, verfuegbar, offen, verspaetet, pickMin, packMin, pickFte, packFte, pickIstMin, packIstMin, pickIstFte, packIstFte, snapshotZeit, isToday, onClose, t, locale }) {
  const erfuellungPct = soll > 0 && erledigt !== null ? Math.round((erledigt / soll) * 100) : null;
  const erfuellungColor = erfuellungPct !== null ? (erfuellungPct >= 100 ? '#10B981' : erfuellungPct >= 80 ? '#F59E0B' : '#EF4444') : null;
  const progressPct = verfuegbar > 0 && erledigt !== null ? Math.min(Math.round((erledigt / verfuegbar) * 100), 100) : 0;
  const hasFte = (pickMin > 0 || packMin > 0);
  const hasIstFte = (pickIstMin > 0 || packIstMin > 0);
  const pickDeltaPct = pickMin > 0 && pickIstMin > 0 ? ((pickIstMin - pickMin) / pickMin) * 100 : null;
  const packDeltaPct = packMin > 0 && packIstMin > 0 ? ((packIstMin - packMin) / packMin) * 100 : null;
  const gesamtSollFte = pickFte + packFte;
  const gesamtIstFte = pickIstFte + packIstFte;
  const gesamtDeltaPct = gesamtSollFte > 0 && gesamtIstFte > 0 ? ((gesamtIstFte - gesamtSollFte) / gesamtSollFte) * 100 : null;
  const snapshotStr = snapshotZeit ? new Date(snapshotZeit).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Vienna' }) : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative bg-surface-elevated border border-line rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: segColors[kunde.segment] }} />
              <span className="text-base font-semibold text-content">{kunde.name}</span>
            </div>
            <div className="text-xs text-[var(--text-secondary)] mt-0.5">{colLabel}{snapshotStr ? ` · ${t('forecast.tooltip.snapshot')} ${snapshotStr}` : ''}</div>
          </div>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl leading-none p-1">&times;</button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* Section 1: Forecast vs. Realität */}
          <div>
            <div className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-[0.5px] mb-2">{t('forecast.popup.forecastVsRealitaet')}</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                <div className="text-xl font-medium text-content">{soll.toLocaleString(locale)}</div>
                <div className="text-[11px] text-[var(--text-secondary)]">SOLL</div>
              </div>
              <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                <div className="text-xl font-medium text-content">{erledigt !== null ? erledigt.toLocaleString(locale) : '–'}</div>
                <div className="text-[11px] text-[var(--text-secondary)]">{t('forecast.popup.erledigt')}</div>
              </div>
              <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                <div className="text-xl font-medium" style={{ color: erfuellungColor || 'var(--text-primary)' }}>
                  {erfuellungPct !== null ? `${erfuellungPct}%` : '–'}
                </div>
                <div className="text-[11px] text-[var(--text-secondary)]">{t('forecast.popup.erfuellung')}</div>
              </div>
            </div>
          </div>

          {/* Section 2: Auftragseingang */}
          {verfuegbar !== null && (
            <div>
              <div className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-[0.5px] mb-2">{t('forecast.popup.auftragseingang')}</div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                  <div className="text-xl font-medium text-content">{verfuegbar.toLocaleString(locale)}</div>
                  <div className="text-[11px] text-[var(--text-secondary)]">{t('forecast.popup.verfuegbar')}</div>
                </div>
                <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                  <div className="text-xl font-medium text-content">{erledigt !== null ? erledigt.toLocaleString(locale) : '–'}</div>
                  <div className="text-[11px] text-[var(--text-secondary)]">{t('forecast.popup.erledigt')}</div>
                </div>
                <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                  <div className="text-xl font-medium text-content">{offen !== null ? offen.toLocaleString(locale) : '–'}</div>
                  <div className="text-[11px] text-[var(--text-secondary)]">{t('forecast.popup.offen')}</div>
                </div>
              </div>
              {/* Progressbar */}
              <div className="h-1.5 bg-[var(--bg-inset,#E5E7EB)] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progressPct}%`, backgroundColor: '#338edc' }} />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[11px] text-[var(--text-secondary)]">{erledigt !== null ? erledigt.toLocaleString(locale) : 0} {t('forecast.popup.abgearbeitet')}</span>
                <span className="text-[11px] text-[var(--text-secondary)] font-medium">{progressPct}%</span>
              </div>
            </div>
          )}

          {/* Section 3: Cutoff-Analyse */}
          <div>
            <div className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-[0.5px] mb-2">{t('forecast.popup.cutoffAnalyse')}</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                <div className="text-xl font-medium text-content">12:00</div>
                <div className="text-[11px] text-[var(--text-secondary)]">{t('forecast.popup.cutoffZeit')}</div>
              </div>
              <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                {verspaetet === null ? (
                  /* Heute vor Arbeitsende — Tag läuft noch */
                  <>
                    <div className="text-xl font-medium text-content">{offen !== null ? offen.toLocaleString(locale) : '–'}</div>
                    <div className="text-[11px] text-[var(--text-secondary)]">{t('forecast.popup.nochOffen')}</div>
                  </>
                ) : verspaetet === 0 ? (
                  /* Alles erledigt */
                  <>
                    <div className="text-xl font-medium" style={{ color: '#10B981' }}>0</div>
                    <div className="text-[11px] text-[var(--text-secondary)]">{t('forecast.popup.alleErledigt')}</div>
                  </>
                ) : (
                  /* Verspätet */
                  <>
                    <div className="text-xl font-medium" style={{ color: '#EF4444' }}>{verspaetet}</div>
                    <div className="text-[11px] text-[var(--text-secondary)]">{t('forecast.popup.verspaetet')}</div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: FTE-Aufwand — SOLL vs IST */}
          {hasFte && (
            <div>
              <div className="text-[11px] font-semibold text-[var(--text-secondary)] uppercase tracking-[0.5px] mb-3">{t('forecast.popup.fteAufwand')}</div>
              {hasIstFte ? (
                <>
                  {/* Header row */}
                  <div className="grid gap-2 mb-2 text-[11px] text-[var(--text-tertiary)] uppercase tracking-wider" style={{ gridTemplateColumns: '68px 1fr 1fr 64px' }}>
                    <div />
                    <div className="text-center">{t('forecast.popup.fteSoll')}</div>
                    <div className="text-center">{t('forecast.popup.fteIst')}</div>
                    <div className="text-center">{t('forecast.popup.fteDelta')}</div>
                  </div>
                  {/* Pick row */}
                  <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: '68px 1fr 1fr 64px' }}>
                    <div className="text-[13px] font-medium text-content self-center">{t('forecast.popup.ftePick')}</div>
                    <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-2.5 text-center">
                      <div className="text-base font-medium text-content">{pickFte.toFixed(2)} FTE</div>
                      <div className="text-[11px] text-[var(--text-secondary)]">{Math.round(pickMin)}min / {(pickMin / 60).toFixed(1)}h</div>
                    </div>
                    <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-2.5 text-center">
                      <div className="text-base font-medium text-content">{pickIstFte.toFixed(2)} FTE</div>
                      <div className="text-[11px] text-[var(--text-secondary)]">{Math.round(pickIstMin)}min / {(pickIstMin / 60).toFixed(1)}h</div>
                    </div>
                    <div className="self-center text-center">
                      <DeltaBadge value={pickDeltaPct} />
                    </div>
                  </div>
                  {/* Pack row */}
                  <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: '68px 1fr 1fr 64px' }}>
                    <div className="text-[13px] font-medium text-content self-center">{t('forecast.popup.ftePack')}</div>
                    <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-2.5 text-center">
                      <div className="text-base font-medium text-content">{packFte.toFixed(2)} FTE</div>
                      <div className="text-[11px] text-[var(--text-secondary)]">{Math.round(packMin)}min / {(packMin / 60).toFixed(1)}h</div>
                    </div>
                    <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-2.5 text-center">
                      <div className="text-base font-medium text-content">{packIstFte.toFixed(2)} FTE</div>
                      <div className="text-[11px] text-[var(--text-secondary)]">{Math.round(packIstMin)}min / {(packIstMin / 60).toFixed(1)}h</div>
                    </div>
                    <div className="self-center text-center">
                      <DeltaBadge value={packDeltaPct} />
                    </div>
                  </div>
                  {/* Gesamt row */}
                  <div className="grid gap-2 pt-2" style={{ gridTemplateColumns: '68px 1fr 1fr 64px', borderTop: '1px solid var(--border-subtle,#E5E7EB)' }}>
                    <div className="text-[13px] font-medium text-content self-center">{t('forecast.popup.fteGesamt')}</div>
                    <div className="text-center self-center">
                      <span className="text-base font-medium text-content">{gesamtSollFte.toFixed(2)} FTE</span>
                    </div>
                    <div className="text-center self-center">
                      <span className="text-base font-medium text-content">{gesamtIstFte.toFixed(2)} FTE</span>
                    </div>
                    <div className="self-center text-center">
                      <DeltaBadge value={gesamtDeltaPct} />
                    </div>
                  </div>
                </>
              ) : (
                /* Fallback: nur SOLL (keine IST-Daten, z.B. zukünftige Tage) */
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                    <div className="text-[11px] text-[var(--text-secondary)] font-medium mb-1">{t('forecast.popup.ftePick')}</div>
                    <div className="text-xl font-medium text-content">{pickFte.toFixed(2)} FTE</div>
                    <div className="text-[11px] text-[var(--text-secondary)]">{Math.round(pickMin)}min / {(pickMin / 60).toFixed(1)}h</div>
                  </div>
                  <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-3 text-center">
                    <div className="text-[11px] text-[var(--text-secondary)] font-medium mb-1">{t('forecast.popup.ftePack')}</div>
                    <div className="text-xl font-medium text-content">{packFte.toFixed(2)} FTE</div>
                    <div className="text-[11px] text-[var(--text-secondary)]">{Math.round(packMin)}min / {(packMin / 60).toFixed(1)}h</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <button onClick={onClose}
            className="w-full py-2.5 rounded-lg text-sm font-medium bg-forecast text-white hover:bg-forecast/90 transition-colors mt-2">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Summary Popup (Segment / GESAMT) ────────────────────────────

function SummaryPopup({ title, titleColor, kundenData, colLabel, onClose, t, locale }) {
  const totals = useMemo(() => {
    let soll = 0, ist = 0, hasIst = false, pickFte = 0, packFte = 0;
    let selberTag = 0, vortagNachCutoff = 0, akkumuliert = 0;
    let verspaetet = 0, backlog = 0, backlogVerspaetet = 0;
    kundenData.forEach((k) => {
      soll += k.soll;
      if (k.ist !== null) { ist += k.ist; hasIst = true; }
      pickFte += k.pickFte; packFte += k.packFte;
      if (k.breakdown) {
        selberTag += k.breakdown.selberTag;
        vortagNachCutoff += k.breakdown.vortagNachCutoff;
        akkumuliert += k.breakdown.akkumuliert;
      }
      verspaetet += k.verspaetet || 0;
      backlog += k.backlog || 0;
      backlogVerspaetet += k.backlogVerspaetet || 0;
    });
    const varianz = hasIst ? ist - soll : null;
    const uebertrag = vortagNachCutoff + akkumuliert;
    const pct = soll > 0 && hasIst ? (ist / soll) * 100 : null;
    const puenktlichkeit = hasIst && ist > 0 ? Math.round(((ist - verspaetet) / ist) * 10000) / 100 : null;
    return { soll, ist, hasIst, pickFte, packFte, selberTag, vortagNachCutoff, akkumuliert, varianz, uebertrag, pct, verspaetet, backlog, backlogVerspaetet, puenktlichkeit };
  }, [kundenData]);

  const color = totals.pct !== null ? ratioColor(totals.pct / 100) : '#338edc';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative bg-surface-elevated border border-line-hover rounded-card w-full max-w-[720px] max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-line">
          <div>
            <div className="text-base font-bold" style={{ color: titleColor }}>{title}</div>
            <div className="text-xs text-[var(--text-secondary)] mt-0.5">{colLabel}</div>
          </div>
          <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-content text-xl leading-none p-1">&times;</button>
        </div>

        <div className="p-5 space-y-3">
          {/* a) IST-SOLL overview */}
          <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
            <div className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('forecast.summary.overview')}</div>
            <div className="flex gap-0 mb-3">
              {[
                { label: t('forecast.detail.done_ist'), value: totals.hasIst ? totals.ist.toLocaleString(locale) : '–' },
                { label: t('forecast.detail.planned_soll'), value: totals.soll.toLocaleString(locale) },
                { label: t('forecast.summary.carryover'), value: totals.uebertrag.toLocaleString(locale) },
                { label: t('common.variance'), value: totals.varianz !== null ? (totals.varianz > 0 ? `+${totals.varianz}` : String(totals.varianz)) : '–' },
              ].map((v) => (
                <div key={v.label} className="flex-1 text-center">
                  <div className="font-mono text-2xl font-extrabold text-content">{v.value}</div>
                  <div className="text-[9px] text-[var(--text-tertiary)] mt-0.5">{v.label}</div>
                </div>
              ))}
            </div>
            {totals.hasIst && totals.soll > 0 && (
              <>
                <div className="text-[10px] text-[var(--text-tertiary)] mb-1">{t('forecast.summary.progress', { pct: totals.pct.toFixed(1) })}</div>
                <div className="h-2 bg-surface-inset rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(totals.pct, 100)}%`, backgroundColor: color }} />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[10px] text-[var(--text-tertiary)] font-mono">0</span>
                  <span className="text-[10px] text-[var(--text-secondary)] font-mono">{t('forecast.summary.expected_total', { n: (totals.soll + totals.uebertrag).toLocaleString(locale) })}</span>
                </div>
              </>
            )}
          </div>

          {/* b) Carryover breakdown */}
          {(totals.uebertrag > 0 || totals.backlog > 0) && (
            <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
              <div className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('forecast.summary.carryover_breakdown')}</div>
              <div className="flex gap-0">
                {[
                  { label: t('forecast.summary.prev_day_missed'), sub: t('forecast.detail.overdue'), value: totals.backlogVerspaetet, clr: '#ef4444' },
                  { label: t('forecast.summary.after_cutoff'), sub: t('forecast.summary.normal'), value: totals.vortagNachCutoff, clr: '#34d399' },
                  { label: t('forecast.summary.we_holiday_pkgs'), sub: t('forecast.summary.normal'), value: totals.akkumuliert, clr: '#7b8ba8' },
                ].map((v) => (
                  <div key={v.label} className="flex-1 text-center">
                    <div className="font-mono text-xl font-extrabold" style={{ color: v.clr }}>{v.value}</div>
                    <div className="text-[9px] text-[var(--text-tertiary)]">{v.sub}</div>
                    <div className="text-[9px] text-[var(--text-tertiary)] mt-0.5">{v.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* c) Late packages */}
          {totals.verspaetet > 0 && (
            <div className="bg-red-500/[0.08] border border-red-500/20 rounded-card px-4 py-3">
              <div className="flex justify-between items-center mb-2">
                <div className="text-xs font-bold text-red-400">{t('forecast.summary.late_sla')}</div>
                <span className="font-mono text-sm font-bold text-red-400">{t('forecast.summary.packages_label', { n: totals.verspaetet.toLocaleString(locale) })}</span>
              </div>
              {totals.puenktlichkeit !== null && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-surface-inset rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(totals.puenktlichkeit, 100)}%`, backgroundColor: totals.puenktlichkeit >= 95 ? '#34d399' : totals.puenktlichkeit >= 85 ? '#eab308' : '#ef4444' }} />
                  </div>
                  <span className="text-[10px] font-mono font-bold" style={{ color: totals.puenktlichkeit >= 95 ? '#34d399' : totals.puenktlichkeit >= 85 ? '#eab308' : '#ef4444' }}>
                    {totals.puenktlichkeit.toFixed(1)}% {t('forecast.detail.on_time')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* d) Customer breakdown */}
          <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
            <div className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('forecast.summary.customer_breakdown')}</div>
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-line-hover">
                    {[
                      { key: 'customer', label: t('forecast.summary.customer'), align: 'text-left' },
                      { key: 'soll', label: t('forecast.summary.soll_label'), align: 'text-right' },
                      { key: 'done', label: t('forecast.summary.done'), align: 'text-right' },
                      { key: 'progress', label: t('forecast.summary.fortschritt'), align: 'text-center' },
                      { key: 'pickh', label: t('forecast.summary.pick_h'), align: 'text-right' },
                      { key: 'packh', label: t('forecast.summary.pack_h'), align: 'text-right' },
                      { key: 'pickpct', label: t('forecast.summary.pick_pct'), align: 'text-right' },
                      { key: 'packpct', label: t('forecast.summary.pack_pct'), align: 'text-right' },
                    ].map((h) => (
                      <th key={h.key} className={`py-2 px-1 font-medium ${h.align} text-[var(--text-secondary)]`}>{h.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kundenData.map((k, i) => {
                    const kPct = k.soll > 0 && k.ist !== null ? (k.ist / k.soll) * 100 : null;
                    const kClr = kPct !== null ? ratioColor(kPct / 100) : null;
                    const pickH = k.pickFte * 7.5;
                    const packH = k.packFte * 7.5;
                    const pickPct = totals.pickFte > 0 ? (k.pickFte / totals.pickFte * 100) : 0;
                    const packPct = totals.packFte > 0 ? (k.packFte / totals.packFte * 100) : 0;
                    return (
                      <tr key={i} className="border-b border-line hover:bg-surface-hover">
                        <td className="py-1.5 px-1 text-content-secondary font-medium">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: segColors[k.segment] }} />
                            <span className="truncate max-w-[120px]">{k.name}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-1 text-right font-mono text-content">{k.soll.toLocaleString(locale)}</td>
                        <td className="py-1.5 px-1 text-right font-mono text-content">{k.ist !== null ? k.ist.toLocaleString(locale) : '–'}</td>
                        <td className="py-1.5 px-1 w-20">
                          {kPct !== null ? (
                            <div className="flex items-center gap-1">
                              <div className="flex-1 h-1.5 bg-surface-inset rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${Math.min(kPct, 100)}%`, backgroundColor: kClr }} />
                              </div>
                              <span className="font-mono text-[10px] font-bold w-7 text-right text-content">{kPct.toFixed(0)}%</span>
                            </div>
                          ) : <span className="text-[var(--text-tertiary)] text-[10px]">–</span>}
                        </td>
                        <td className="py-1.5 px-1 text-right font-mono text-content">{pickH.toFixed(1)}h</td>
                        <td className="py-1.5 px-1 text-right font-mono text-content">{packH.toFixed(1)}h</td>
                        <td className="py-1.5 px-1 text-right font-mono text-[var(--text-secondary)]">{pickPct.toFixed(0)}%</td>
                        <td className="py-1.5 px-1 text-right font-mono text-[var(--text-secondary)]">{packPct.toFixed(0)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-line-hover">
                    <td className="py-2 px-1 font-bold text-content-tertiary">{t('forecast.summary.total_row')}</td>
                    <td className="py-2 px-1 text-right font-mono font-bold text-content">{totals.soll.toLocaleString(locale)}</td>
                    <td className="py-2 px-1 text-right font-mono font-bold text-content">{totals.hasIst ? totals.ist.toLocaleString(locale) : '–'}</td>
                    <td className="py-2 px-1">
                      {totals.pct !== null && (
                        <div className="flex items-center gap-1">
                          <div className="flex-1 h-1.5 bg-surface-inset rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${Math.min(totals.pct, 100)}%`, backgroundColor: color }} />
                          </div>
                          <span className="font-mono text-[10px] font-bold w-7 text-right text-content">{totals.pct.toFixed(0)}%</span>
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-1 text-right font-mono font-bold text-content">{(totals.pickFte * 7.5).toFixed(1)}h</td>
                    <td className="py-2 px-1 text-right font-mono font-bold text-content">{(totals.packFte * 7.5).toFixed(1)}h</td>
                    <td className="py-2 px-1 text-right font-mono font-bold text-[var(--text-secondary)]">100%</td>
                    <td className="py-2 px-1 text-right font-mono font-bold text-[var(--text-secondary)]">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* e) Package data */}
          <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
            <div className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('forecast.summary.package_data')}</div>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('forecast.summary.prev_after_cutoff')}</span><span className="font-mono font-medium text-content">{totals.vortagNachCutoff.toLocaleString(locale)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('forecast.summary.same_day_pkgs')}</span><span className="font-mono font-medium text-content">{totals.selberTag.toLocaleString(locale)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--text-secondary)]">{t('forecast.summary.accumulated_we')}</span><span className="font-mono font-medium text-content">{totals.akkumuliert.toLocaleString(locale)}</span></div>
              <div className="border-t border-line mt-2 pt-2 flex justify-between">
                <span className="text-content-tertiary font-medium">{t('forecast.summary.total_packages')}</span>
                <span className="font-mono font-bold" style={{ color: titleColor }}>{totals.soll.toLocaleString(locale)}</span>
              </div>
            </div>
          </div>

          {/* f) Resources */}
          <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
            <div className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('common.resources')}</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <div className="text-[9px] text-primary-400/70 font-bold uppercase mb-1">{t('forecast.detail.picking')}</div>
                <div className="font-mono text-lg font-bold text-primary-400">{(totals.pickFte * 7.5).toFixed(1)}h</div>
                <div className="text-[10px] text-[var(--text-tertiary)]">{totals.pickFte.toFixed(2)} FTE</div>
              </div>
              <div className="text-center">
                <div className="text-[9px] text-purple-400/70 font-bold uppercase mb-1">{t('forecast.detail.packing')}</div>
                <div className="font-mono text-lg font-bold text-purple-400">{(totals.packFte * 7.5).toFixed(1)}h</div>
                <div className="text-[10px] text-[var(--text-tertiary)]">{totals.packFte.toFixed(2)} FTE</div>
              </div>
              <div className="text-center">
                <div className="text-[9px] text-content-tertiary font-bold uppercase mb-1">{t('common.total')}</div>
                <div className="font-mono text-lg font-bold text-content">{((totals.pickFte + totals.packFte) * 7.5).toFixed(1)}h</div>
                <div className="text-[10px] text-[var(--text-tertiary)]">{(totals.pickFte + totals.packFte).toFixed(2)} FTE</div>
              </div>
            </div>
          </div>

          <button onClick={onClose} className="w-full py-2.5 rounded-lg text-sm font-bold bg-forecast text-white hover:bg-forecast/90 transition-colors mt-2">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// AI SUGGESTION PANEL
// ═══════════════════════════════════════════════════════════════════

function AiSuggestionPanel({ year, month, monatsData, onApplied, rampupMap = {}, t, locale }) {
  const [collapsed, setCollapsed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [applied, setApplied] = useState(new Set());
  const [applying, setApplying] = useState(false);

  // Reset when month changes
  useEffect(() => {
    setSuggestions(null);
    setExplanation(null);
    setMetadata(null);
    setApplied(new Set());
  }, [year, month]);

  const targetMonth = `${year}-${String(month).padStart(2, '0')}`;

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const data = await api('/api/forecast/ai-suggestion', {
        method: 'POST',
        body: { month: targetMonth, segment: segmentFilter },
      });
      setSuggestions(data.suggestions || []);
      setExplanation(data.explanation || null);
      setMetadata(data.metadata || null);
      setApplied(new Set());
    } catch (err) {
      console.error('AI Suggestion error:', err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const applySingle = async (s) => {
    setApplying(true);
    try {
      await api('/api/forecast/ai-apply', {
        method: 'POST',
        body: { month: targetMonth, suggestions: [{ kunde_id: s.kunde_id, pakete: s.pakete, standort_id: s.standort_id }] },
      });
      setApplied(prev => new Set([...prev, s.kunde_id]));
      onApplied();
    } catch (err) {
      console.error('Apply error:', err);
    } finally {
      setApplying(false);
    }
  };

  const applyBulk = async (filter) => {
    if (!suggestions) return;
    setApplying(true);
    try {
      let toApply = suggestions.filter(s => s.pakete > 0 && !applied.has(s.kunde_id));
      if (filter === 'C') toApply = toApply.filter(s => s.segment === 'C');
      if (filter === 'rampup') toApply = toApply.filter(s => s.is_rampup);
      if (!toApply.length) return;
      await api('/api/forecast/ai-apply', {
        method: 'POST',
        body: {
          month: targetMonth,
          suggestions: toApply.map(s => ({ kunde_id: s.kunde_id, pakete: s.pakete, standort_id: s.standort_id })),
        },
      });
      setApplied(prev => new Set([...prev, ...toApply.map(s => s.kunde_id)]));
      onApplied();
    } catch (err) {
      console.error('Bulk apply error:', err);
    } finally {
      setApplying(false);
    }
  };

  const filteredSuggestions = suggestions
    ? suggestions.filter(s => segmentFilter === 'all' || s.segment === segmentFilter)
    : [];

  return (
    <div className="mb-5 bg-surface-card rounded-xl shadow-card overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center gap-2 hover:bg-surface-hover transition-colors"
        style={{ padding: '14px 20px' }}
      >
        <span style={{ fontSize: 16 }}>✨</span>
        <span className="text-[13px] font-medium" style={{ color: '#10B981' }}>{t('forecast.ai.title')}</span>
        {suggestions && (
          <span className="text-[11px] text-content-tertiary ml-0.5">
            {t('forecast.ai.customers_count', { n: filteredSuggestions.length })}
          </span>
        )}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={`ml-auto text-content-tertiary transition-transform ${collapsed ? '' : 'rotate-180'}`}>
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>

      {/* Body */}
      {!collapsed && (
        <div className="px-4 pb-4 border-t border-indigo-500/10">
          {/* Controls */}
          <div className="flex items-center gap-2 mt-3 mb-3 flex-wrap">
            <div className="flex bg-surface-inset rounded-lg p-0.5 border border-line-hover">
              {[{ key: 'all', label: t('forecast.ai.all_filter') }, { key: 'A', label: 'A' }, { key: 'B', label: 'B' }, { key: 'C', label: 'C' }].map(s => (
                <button key={s.key} onClick={() => setSegmentFilter(s.key)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${segmentFilter === s.key ? 'bg-indigo-500/20 text-indigo-300' : 'text-[var(--text-secondary)] hover:text-content-secondary'}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <button
              onClick={loadSuggestions}
              disabled={loading}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {loading ? (
                <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a4 4 0 014 4c0 1.5-.8 2.8-2 3.5V11h3a3 3 0 013 3v1"/><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/></svg>
              )}
              {t('forecast.ai.load')}
            </button>
          </div>

          {/* Loading skeleton */}
          {loading && (
            <div className="space-y-2">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-8 rounded-lg bg-surface-inset animate-pulse" />
              ))}
            </div>
          )}

          {/* Results */}
          {!loading && suggestions && (
            <>
              {/* AI Explanation */}
              {explanation && (
                <div className="mb-3 p-3 rounded-card bg-indigo-500/[0.08] border border-indigo-500/15">
                  <div className="flex items-start gap-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-indigo-400 mt-0.5 flex-shrink-0">
                      <path d="M12 2a4 4 0 014 4c0 1.5-.8 2.8-2 3.5V11h3a3 3 0 013 3v1.5a2.5 2.5 0 01-5 0V14h-2v1.5a2.5 2.5 0 01-5 0V14H6v1.5a2.5 2.5 0 01-5 0V14a3 3 0 013-3h3V9.5A4 4 0 0112 2z"/>
                    </svg>
                    <p className="text-xs text-content-secondary leading-relaxed">{explanation}</p>
                  </div>
                </div>
              )}

              {/* Metadata */}
              {metadata && (
                <div className="flex items-center gap-3 mb-3 text-[10px] text-[var(--text-tertiary)] flex-wrap">
                  <span>{metadata.total_kunden} {t('common.customers')}</span>
                  <span>{(metadata.total_pakete || 0).toLocaleString(locale)} {t('common.packages')}</span>
                  <span className="text-emerald-400">{metadata.confidence_breakdown?.high || 0} {t('forecast.ai.metadata.high')}</span>
                  <span className="text-amber-400">{metadata.confidence_breakdown?.medium || 0} {t('forecast.ai.metadata.medium')}</span>
                  <span className="text-red-400">{metadata.confidence_breakdown?.low || 0} {t('forecast.ai.metadata.low')}</span>
                  {metadata.rampup_count > 0 && <span className="text-cyan-400">{metadata.rampup_count} {t('forecast.ai.rampup_phase')}</span>}
                </div>
              )}

              {/* Bulk action buttons */}
              {filteredSuggestions.some(s => s.pakete > 0 && !applied.has(s.kunde_id)) && (
                <div className="flex items-center gap-2 mb-3">
                  <button onClick={() => applyBulk('all')} disabled={applying}
                    className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-forecast/15 text-forecast hover:bg-forecast/25 transition-colors disabled:opacity-50">
                    {t('forecast.ai.apply_all')}
                  </button>
                  {filteredSuggestions.some(s => s.segment === 'C' && s.pakete > 0 && !applied.has(s.kunde_id)) && (
                    <button onClick={() => applyBulk('C')} disabled={applying}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 transition-colors disabled:opacity-50">
                      {t('forecast.ai.autofill_c')}
                    </button>
                  )}
                  {filteredSuggestions.some(s => s.is_rampup && s.pakete > 0 && !applied.has(s.kunde_id)) && (
                    <button onClick={() => applyBulk('rampup')} disabled={applying}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25 transition-colors disabled:opacity-50">
                      {t('forecast.ai.autofill_rampup')}
                    </button>
                  )}
                </div>
              )}

              {/* Table */}
              {filteredSuggestions.length > 0 ? (
                <div className="overflow-x-auto rounded-card border border-line">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-surface-inset text-[var(--text-tertiary)]">
                        <th className="text-left px-2.5 py-1.5 font-medium">{t('forecast.ai.table.customer')}</th>
                        <th className="text-center px-2 py-1.5 font-medium w-12">{t('forecast.ai.table.seg')}</th>
                        <th className="text-right px-2 py-1.5 font-medium w-20">{t('forecast.ai.table.current')}</th>
                        <th className="text-right px-2 py-1.5 font-medium w-20">{t('forecast.ai.table.suggestion')}</th>
                        <th className="text-right px-2 py-1.5 font-medium w-16">{t('forecast.ai.table.delta')}</th>
                        <th className="text-center px-2 py-1.5 font-medium w-20">{t('forecast.ai.table.confidence')}</th>
                        <th className="text-left px-2 py-1.5 font-medium">{t('forecast.ai.table.basis')}</th>
                        <th className="text-center px-2 py-1.5 font-medium w-20">{t('forecast.ai.table.action')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSuggestions.map(s => {
                        const delta = s.aktuell != null ? s.pakete - s.aktuell : null;
                        const isApplied = applied.has(s.kunde_id);
                        return (
                          <tr key={s.kunde_id} className={`border-t border-line/50 hover:bg-surface-hover/50 transition-colors ${isApplied ? 'opacity-60' : ''}`}>
                            <td className="px-2.5 py-1.5 font-medium text-content truncate max-w-[180px]">{s.kunde_name}</td>
                            <td className="text-center px-2 py-1.5">
                              <div className="flex items-center justify-center gap-1">
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold" style={{ color: segColors[s.segment] || '#6366f1', backgroundColor: (segColors[s.segment] || '#6366f1') + '20' }}>
                                  {s.segment}
                                </span>
                                {s.is_rampup && (
                                  <span className="inline-flex items-center px-1 py-0.5 rounded text-[8px] font-bold bg-cyan-500/12 text-cyan-400" title={t('forecast.ai.rampup_phase')}>
                                    {t('forecast.ai.rampup')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="text-right px-2 py-1.5 font-mono text-content-secondary">{s.aktuell != null ? s.aktuell.toLocaleString(locale) : '–'}</td>
                            <td className="text-right px-2 py-1.5 font-mono font-semibold text-content">{s.pakete > 0 ? s.pakete.toLocaleString(locale) : '–'}</td>
                            <td className={`text-right px-2 py-1.5 font-mono text-[11px] ${delta != null ? (delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-[var(--text-tertiary)]') : 'text-[var(--text-tertiary)]'}`}>
                              {delta != null ? (delta > 0 ? `+${delta.toLocaleString(locale)}` : delta.toLocaleString(locale)) : '–'}
                            </td>
                            <td className="text-center px-2 py-1.5"><ConfidenceBadge level={s.confidence} t={t} /></td>
                            <td className="px-2 py-1.5 text-[var(--text-tertiary)] text-[10px] truncate max-w-[200px]" title={s.reasoning}>{s.reasoning}</td>
                            <td className="text-center px-2 py-1.5">
                              {isApplied ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-emerald-400 mx-auto"><path d="M20 6L9 17l-5-5"/></svg>
                              ) : s.pakete > 0 ? (
                                <button onClick={() => applySingle(s)} disabled={applying}
                                  className="px-2 py-0.5 rounded text-[10px] font-semibold bg-forecast/15 text-forecast hover:bg-forecast/25 transition-colors disabled:opacity-50">
                                  {t('forecast.ai.apply')}
                                </button>
                              ) : (
                                <span className="text-[var(--text-tertiary)] text-[10px]">–</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6 text-[var(--text-tertiary)] text-xs">{t('forecast.ai.no_suggestions')}</div>
              )}
            </>
          )}

          {/* Empty state */}
          {!loading && !suggestions && (
            <div className="text-center py-4 text-[var(--text-tertiary)] text-xs">
              {t('forecast.ai.empty_hint')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FORECAST GRID  (Tag | Woche | Monat) — Compact v2
// ═══════════════════════════════════════════════════════════════════

function ForecastGrid({ gridData, monatsData, year, month, viewMode, setViewMode, liveData, intradayData, showWeekends, weekendActivity, rampupMap = {}, fteBedarf = null, demandMap = {}, aktionen = [], feiertageGrid = [], t, locale, tMonth, tMonthShort }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [collapsedSegs, setCollapsedSegs] = useState({});
  const [selectedKw, setSelectedKw] = useState(null);
  const [popup, setPopup] = useState(null);
  const [segPopup, setSegPopup] = useState(null);
  const [gridTooltip, setGridTooltip] = useState(null);
  const [searchKunde, setSearchKunde] = useState(() => searchParams.get('kunde') || '');
  const [filterSegment, setFilterSegment] = useState('');
  const toggleSeg = (seg) => setCollapsedSegs((prev) => ({ ...prev, [seg]: !prev[seg] }));

  useEffect(() => { if (viewMode !== 'tag') setSelectedKw(null); }, [viewMode]);

  // Build live data map: kunde_id → { erledigt, offen }
  const liveMap = useMemo(() => {
    const m = {};
    if (liveData?.kunden) liveData.kunden.forEach(k => { m[k.kunde_id] = k; });
    return m;
  }, [liveData]);

  // Build intraday map: kunde_id → { pickings_done, pickings_todo, fortschritt_pct, status }
  const intradayMap = useMemo(() => {
    const m = {};
    if (Array.isArray(intradayData)) intradayData.forEach(d => { m[d.kunde_id] = d; });
    return m;
  }, [intradayData]);

  // Detect live grid data and latest snapshot time
  const hasLiveGrid = useMemo(() => gridData.some(r => r.is_live), [gridData]);
  const liveSnapshotTime = useMemo(() => {
    const liveTimes = gridData.filter(r => r.is_live && r.live_snapshot_time).map(r => new Date(r.live_snapshot_time));
    return liveTimes.length > 0 ? new Date(Math.max(...liveTimes)) : null;
  }, [gridData]);

  // ── Quality scores (monthly summary) ──
  const [qualityScores, setQualityScores] = useState({});
  const [qualityConfig, setQualityConfig] = useState(null);
  useEffect(() => {
    const datumParam = `${year}-${String(month).padStart(2, '0')}`;
    fetch(`/api/forecast/quality/summary?periode=monat&datum=${datumParam}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        const m = {};
        (d.kunden || []).forEach(k => { m[k.kunde_id] = k; });
        setQualityScores(m);
      }).catch(() => {});
    fetch('/api/forecast/quality/config', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.config) return;
        const cfg = {};
        d.config.forEach(c => { cfg[c.key] = typeof c.value === 'string' ? (isNaN(c.value) ? c.value : parseFloat(c.value)) : c.value; });
        setQualityConfig(cfg);
      }).catch(() => {});
  }, [year, month]);

  // ── Feiertag Map ──
  const feiertagMap = useMemo(() => {
    const m = new Map();
    for (const f of feiertageGrid) {
      const ds = typeof f.datum === 'string' ? f.datum.slice(0, 10) : '';
      if (ds) m.set(ds, f.name);
    }
    return m;
  }, [feiertageGrid]);

  // ── Days ──
  const daysInMonth = new Date(year, month, 0).getDate();
  const allDays = useMemo(() =>
    Array.from({ length: daysInMonth }, (_, i) => {
      const d = new Date(year, month - 1, i + 1);
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
      const feiertagName = feiertagMap.get(dateStr) || null;
      return { num: i + 1, dateStr, isWeekend: d.getDay() === 0 || d.getDay() === 6, isFeiertag: !!feiertagName, feiertagName, dayLabel: t(`common.weekdays.${d.getDay()}`), kw: getKW(d) };
    }), [year, month, daysInMonth, feiertagMap]);

  const workDays = useMemo(() => allDays.filter((d) => !d.isWeekend && !d.isFeiertag), [allDays]);

  // Weekend dates that have actual IST data (gearbeitet am Wochenende)
  // Sources: 1) tages_forecasts IST data, 2) pack_stats/waves from backend
  const weekendsWithIst = useMemo(() => {
    const set = new Set();
    // From grid data (tages_forecasts with ist_pakete)
    gridData.forEach(row => {
      if (parseFloat(row.ist_pakete) > 0) {
        const d = parseDatum(row.datum);
        if (d) {
          const dt = new Date(d + 'T12:00:00');
          if (dt.getDay() === 0 || dt.getDay() === 6) set.add(d);
        }
      }
    });
    // From weekend-activity API (pack_stats + waves)
    if (weekendActivity) weekendActivity.forEach(d => set.add(d));
    return set;
  }, [gridData, weekendActivity]);

  const visibleDays = useMemo(() => {
    if (showWeekends) return allDays;
    // Werktage + Wochenenden/Feiertage mit IST-Daten
    return allDays.filter(d => (!d.isWeekend && !d.isFeiertag) || weekendsWithIst.has(d.dateStr));
  }, [showWeekends, allDays, weekendsWithIst]);

  const weeks = useMemo(() => {
    const m = new Map();
    allDays.forEach((d) => { if (!m.has(d.kw)) m.set(d.kw, []); m.get(d.kw).push(d); });
    return [...m.entries()].map(([kw, days]) => ({ kw, days, workDays: days.filter((d) => !d.isWeekend && !d.isFeiertag) })).filter((w) => w.workDays.length > 0).sort((a, b) => a.kw - b.kw);
  }, [allDays]);

  // ── Columns ──
  const columns = useMemo(() => {
    if (viewMode === 'monat') return [{ type: 'monat', key: 'monat', label: tMonthShort(month - 1) }];
    if (viewMode === 'tag') {
      const days = selectedKw ? visibleDays.filter((d) => d.kw === selectedKw) : visibleDays;
      return days.map((d) => ({ type: 'tag', key: d.dateStr, day: d, label: `${d.dayLabel} ${d.num}`, isWeekend: d.isWeekend, isHoliday: d.isFeiertag, holidayName: d.feiertagName }));
    }
    return weeks.map((w) => {
      const f = w.workDays[0], l = w.workDays[w.workDays.length - 1];
      return { type: 'kw', key: `kw-${w.kw}`, kw: w.kw, label: `KW ${w.kw}`, sublabel: `${f.num}.–${l.num}.`, workDays: w.workDays };
    });
  }, [viewMode, selectedKw, visibleDays, weeks, month]);

  // ── Kunden ──
  const kunden = useMemo(() => {
    const map = new Map();
    gridData.forEach((row) => {
      if (!map.has(row.kunde_id)) map.set(row.kunde_id, { id: row.kunde_id, name: row.kunde_name, segment: row.kunde_segment, days: {} });
      const d = parseDatum(row.datum);
      if (d) map.get(row.kunde_id).days[d] = row;
    });
    // Ensure all customers with monats_forecasts appear, even without tages_forecasts
    monatsData.forEach((mf) => {
      if (!map.has(mf.kunde_id)) {
        map.set(mf.kunde_id, { id: mf.kunde_id, name: mf.kunde_name, segment: mf.kunde_segment || 'B', days: {} });
      }
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [gridData, monatsData]);

  const segments = useMemo(() => {
    const s = {};
    kunden.forEach((k) => { const seg = k.segment || 'B'; if (!s[seg]) s[seg] = []; s[seg].push(k); });
    return s;
  }, [kunden]);

  // Aktionszeiträume: per-date and per-kunde+date maps
  const aktionenDayMap = useMemo(() => {
    const dayMap = new Map(); // dateStr → [{ kunde_name, titel, typ, multiplikator }]
    const kundeDayMap = new Map(); // "kundeId-dateStr" → aktion
    for (const a of aktionen) {
      const von = a.datum_von?.slice(0, 10);
      const bis = a.datum_bis?.slice(0, 10);
      if (!von || !bis) continue;
      // Iterate days
      const start = new Date(von + 'T12:00:00');
      const end = new Date(bis + 'T12:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const ds = d.toISOString().slice(0, 10);
        if (!dayMap.has(ds)) dayMap.set(ds, []);
        dayMap.get(ds).push(a);
        kundeDayMap.set(`${a.kunde_id}-${ds}`, a);
      }
    }
    return { dayMap, kundeDayMap };
  }, [aktionen]);

  const filteredKunden = useMemo(() => {
    let list = kunden;
    if (searchKunde) {
      const q = searchKunde.toLowerCase();
      list = list.filter(k => k.name.toLowerCase().includes(q));
    }
    if (filterSegment) {
      list = list.filter(k => (k.segment || 'B') === filterSegment);
    }
    return list;
  }, [kunden, searchKunde, filterSegment]);

  const filteredSegments = useMemo(() => {
    const s = {};
    filteredKunden.forEach(k => { const seg = k.segment || 'B'; if (!s[seg]) s[seg] = []; s[seg].push(k); });
    return s;
  }, [filteredKunden]);

  const segOrder = ['A', 'B', 'C'];
  const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Vienna' });

  // ── Cell value aggregation ──
  function cellVal(kunde, col) {
    if (col.type === 'tag') {
      const c = kunde.days[col.day.dateStr];
      if (!c) return { soll: 0, ist: null, pf: 0, af: 0, pickMin: 0, packMin: 0, spitze: false, verspaetet: 0, backlog: 0, backlogVerspaetet: 0, backlogPlanmaessig: 0, backlogAkkumuliert: 0, puenktlichkeitsrate: 0, cutoffStunde: 0, cutoffOffen: 0, cutoffErledigt: 0, isLive: false, livePickingsTodo: 0, verfuegbar: null, offen: null, snapshotZeit: null, verspaetetSnapshot: null, pickIstMin: 0, packIstMin: 0, pickIstFte: 0, packIstFte: 0, raw: null };
      return {
        soll: Number(c.soll_pakete) || 0, ist: c.ist_pakete != null ? Number(c.ist_pakete) : null,
        pf: Number(c.benoetigte_pick_fte) || 0, af: Number(c.benoetigte_pack_fte) || 0,
        pickMin: Number(c.picking_minuten) || 0, packMin: Number(c.packing_minuten) || 0,
        spitze: !!c.ist_spitzentag,
        verspaetet: Number(c.verspaetete_pakete) || 0,
        backlog: Number(c.backlog_gesamt) || 0,
        backlogVerspaetet: Number(c.backlog_verspaetet) || 0,
        backlogPlanmaessig: Number(c.backlog_planmaessig) || 0,
        backlogAkkumuliert: Number(c.backlog_akkumuliert) || 0,
        puenktlichkeitsrate: Number(c.puenktlichkeitsrate) || 0,
        cutoffStunde: Number(c.cutoff_stunde) || Number(c.kunde_cutoff_stunde) || 0,
        cutoffOffen: Number(c.cutoff_offen) || 0,
        cutoffErledigt: Number(c.cutoff_erledigt) || 0,
        isLive: !!c.is_live,
        livePickingsTodo: Number(c.live_pickings_todo) || 0,
        verfuegbar: c.verfuegbar != null ? Number(c.verfuegbar) : null,
        offen: c.offen != null ? Number(c.offen) : null,
        snapshotZeit: c.snapshot_zeit || null,
        verspaetetSnapshot: c.verspaetet_snapshot != null ? Number(c.verspaetet_snapshot) : null,
        pickIstMin: Number(c.pick_ist_min) || 0,
        packIstMin: Number(c.pack_ist_min) || 0,
        pickIstFte: Number(c.pick_ist_fte) || 0,
        packIstFte: Number(c.pack_ist_fte) || 0,
        raw: c,
      };
    }
    const days = col.type === 'kw' ? col.workDays : workDays;
    let soll = 0, ist = 0, istN = 0, pf = 0, af = 0, pickMin = 0, packMin = 0, spitze = false;
    let verspaetet = 0, backlog = 0, backlogVerspaetet = 0, backlogPlanmaessig = 0, backlogAkkumuliert = 0;
    let cutoffOffen = 0, cutoffErledigt = 0, puenktSumIst = 0, puenktSumRate = 0;
    let verfuegbar = 0, verfuegbarN = 0, offen = 0, offenN = 0, verspaetetSnap = 0, verspaetetSnapN = 0;
    let pickIstMin = 0, packIstMin = 0, pickIstFte = 0, packIstFte = 0;
    days.forEach((d) => {
      const c = kunde.days[d.dateStr];
      if (c) {
        soll += Number(c.soll_pakete) || 0;
        if (c.ist_pakete != null) { ist += Number(c.ist_pakete); istN++; }
        pf += Number(c.benoetigte_pick_fte) || 0; af += Number(c.benoetigte_pack_fte) || 0;
        pickMin += Number(c.picking_minuten) || 0; packMin += Number(c.packing_minuten) || 0;
        if (c.ist_spitzentag) spitze = true;
        verspaetet += Number(c.verspaetete_pakete) || 0;
        backlog += Number(c.backlog_gesamt) || 0;
        backlogVerspaetet += Number(c.backlog_verspaetet) || 0;
        backlogPlanmaessig += Number(c.backlog_planmaessig) || 0;
        backlogAkkumuliert += Number(c.backlog_akkumuliert) || 0;
        cutoffOffen += Number(c.cutoff_offen) || 0;
        cutoffErledigt += Number(c.cutoff_erledigt) || 0;
        if (c.verfuegbar != null) { verfuegbar += Number(c.verfuegbar); verfuegbarN++; }
        if (c.offen != null) { offen += Number(c.offen); offenN++; }
        if (c.verspaetet_snapshot != null) { verspaetetSnap += Number(c.verspaetet_snapshot); verspaetetSnapN++; }
        pickIstMin += Number(c.pick_ist_min) || 0;
        packIstMin += Number(c.pack_ist_min) || 0;
        pickIstFte += Number(c.pick_ist_fte) || 0;
        packIstFte += Number(c.pack_ist_fte) || 0;
        if (c.ist_pakete != null && Number(c.puenktlichkeitsrate) > 0) {
          puenktSumIst += Number(c.ist_pakete);
          puenktSumRate += Number(c.puenktlichkeitsrate) * Number(c.ist_pakete);
        }
      }
    });
    const puenktlichkeitsrate = puenktSumIst > 0 ? puenktSumRate / puenktSumIst : 0;
    return { soll, ist: istN > 0 ? ist : null, pf, af, pickMin, packMin, spitze, verspaetet, backlog, backlogVerspaetet, backlogPlanmaessig, backlogAkkumuliert, puenktlichkeitsrate, cutoffStunde: 0, cutoffOffen, cutoffErledigt, isLive: false, livePickingsTodo: 0, verfuegbar: verfuegbarN > 0 ? verfuegbar : null, offen: offenN > 0 ? offen : null, snapshotZeit: null, verspaetetSnapshot: verspaetetSnapN > 0 ? verspaetetSnap : null, pickIstMin, packIstMin, pickIstFte, packIstFte, raw: null };
  }

  function colPakete(col) { let s = 0; kunden.forEach((k) => { s += cellVal(k, col).soll; }); return s; }
  function colFte(col) { let p = 0, a = 0; kunden.forEach((k) => { const v = cellVal(k, col); p += v.pf; a += v.af; }); return { pick: p, pack: a, total: p + a }; }
  function segColTotal(segK, col) {
    let s = 0, ist = 0, istN = 0, v = 0, b = 0, bv = 0, bp = 0, ba = 0, lpt = 0, hasLive = false, verf = 0, verfN = 0, off = 0, offN = 0, vSnap = 0;
    segK.forEach((k) => { const cv = cellVal(k, col); s += cv.soll; if (cv.ist != null) { ist += cv.ist; istN++; } v += cv.verspaetet; b += cv.backlog; bv += cv.backlogVerspaetet; bp += cv.backlogPlanmaessig; ba += cv.backlogAkkumuliert; lpt += cv.livePickingsTodo; if (cv.isLive) hasLive = true; if (cv.verfuegbar != null) { verf += cv.verfuegbar; verfN++; } if (cv.offen != null) { off += cv.offen; offN++; } vSnap += cv.verspaetetSnapshot || 0; });
    return { soll: s, ist: istN > 0 ? ist : null, verspaetet: v, backlog: b, backlogVerspaetet: bv, backlogPlanmaessig: bp, backlogAkkumuliert: ba, livePickingsTodo: lpt, hasLive, verfuegbar: verfN > 0 ? verf : null, offen: offN > 0 ? off : null, verspaetetSnapshot: vSnap };
  }

  function colLateStats(col) {
    let late = 0, istGesamt = 0, cutoffDone = 0, cutoffTotal = 0;
    const proKunde = [];
    let cutoffStunde = 0;
    // For today's column: use live intraday data (pickings_todo = remaining/late)
    const isToday = col.type === 'tag' && col.day.dateStr === today;
    const hasIntraday = isToday && Object.keys(intradayMap).length > 0;
    kunden.forEach((k) => {
      if (hasIntraday) {
        const intra = intradayMap[k.id];
        if (intra) {
          const done = Number(intra.pickings_done) || 0;
          const todo = Number(intra.pickings_todo) || 0;
          const gesamt = done + todo;
          late += todo;
          istGesamt += gesamt;
          cutoffDone += done;
          cutoffTotal += gesamt;
          if (todo > 0) proKunde.push({ name: k.name, late: todo });
        }
      } else {
        const v = cellVal(k, col);
        late += v.verspaetet;
        if (v.ist != null) istGesamt += v.ist;
        cutoffDone += v.cutoffErledigt;
        cutoffTotal += v.cutoffErledigt + v.cutoffOffen;
        if (v.verspaetet > 0) proKunde.push({ name: k.name, late: v.verspaetet });
        if (v.cutoffStunde > cutoffStunde) cutoffStunde = v.cutoffStunde;
      }
    });
    proKunde.sort((a, b) => b.late - a.late);
    const latePct = istGesamt > 0 ? (late / istGesamt) * 100 : 0;
    const hasData = hasIntraday || istGesamt > 0;
    return { late, istGesamt, latePct, cutoffDone, cutoffTotal, cutoffStunde, proKunde, hasData };
  }

  function fteDisplay(fte, col, field) {
    const raw = fte[field];
    if (col.type === 'kw') return raw / (col.workDays?.length || 1);
    if (col.type === 'monat') return raw / (workDays.length || 1);
    return raw;
  }

  function peakDailyFte(field) {
    let max = 0;
    workDays.forEach((d) => { let s = 0; kunden.forEach((k) => { const c = k.days[d.dateStr]; if (c) s += Number(c[field]) || 0; }); if (s > max) max = s; });
    return max;
  }

  // ── Benchmark FTE map ──
  const benchFteMap = useMemo(() => {
    const m = {};
    if (fteBedarf?.days) fteBedarf.days.forEach(d => { m[d.datum] = d; });
    return m;
  }, [fteBedarf]);

  function colBenchFte(col) {
    if (col.type === 'tag') {
      const d = benchFteMap[col.day.dateStr];
      return d ? { bedarf: d.fte_bedarf, geplant: d.geplant_fte, delta: d.delta, status: d.status, topCustomers: d.top_customers } : null;
    }
    const days = col.type === 'kw' ? col.workDays : workDays;
    let bedarfSum = 0, geplantSum = 0, count = 0;
    days.forEach(d => { const bf = benchFteMap[d.dateStr]; if (bf) { bedarfSum += bf.fte_bedarf; geplantSum += bf.geplant_fte; count++; } });
    if (!count) return null;
    const avgBedarf = bedarfSum / count;
    const avgGeplant = geplantSum / count;
    const delta = avgGeplant - avgBedarf;
    return { bedarf: avgBedarf, geplant: avgGeplant, delta, status: delta < -1 ? 'red' : delta < 0 ? 'orange' : 'green', topCustomers: null };
  }

  // ── Cell border color ──
  function borderColor(v) {
    if (v.soll === 0 || v.ist === null) return 'transparent';
    return ratioColor(v.ist / v.soll);
  }

  // Get demand data for a customer on a specific day or aggregated across days
  function getDemand(kundeName, col) {
    const key = (kundeName || '').toLowerCase().trim();
    if (!key) return null;
    if (col.type === 'tag') {
      const dayMap = demandMap[col.day?.dateStr];
      return dayMap?.[key] || null;
    }
    // Week/Month: aggregate
    const days = col.type === 'kw' ? col.workDays : workDays;
    let demand = 0, done = 0, todo = 0, found = false;
    days.forEach(d => {
      const dayMap = demandMap[d.dateStr];
      const entry = dayMap?.[key];
      if (entry) { demand += entry.demand; done += entry.done; todo += entry.todo; found = true; }
    });
    return found ? { demand, done, todo } : null;
  }

  // Get total demand for all customers on a column
  function colDemand(col) {
    if (col.type === 'tag') {
      const dayMap = demandMap[col.day?.dateStr];
      if (!dayMap) return { demand: 0, done: 0, todo: 0, hasData: false };
      let demand = 0, done = 0, todo = 0;
      Object.values(dayMap).forEach(v => { demand += v.demand; done += v.done; todo += v.todo; });
      return { demand, done, todo, hasData: demand > 0 || done > 0 };
    }
    const days = col.type === 'kw' ? col.workDays : workDays;
    let demand = 0, done = 0, todo = 0, hasData = false;
    days.forEach(d => {
      const dayMap = demandMap[d.dateStr];
      if (dayMap) Object.values(dayMap).forEach(v => { demand += v.demand; done += v.done; todo += v.todo; hasData = true; });
    });
    return { demand, done, todo, hasData };
  }

  // ── Open popup ──
  function openPopup(kunde, col) {
    const v = cellVal(kunde, col);
    if (v.soll === 0) return;
    setGridTooltip(null);

    const popupIsToday = col.type === 'tag' && col.day?.dateStr === today;

    setPopup({
      kunde, colLabel: col.label,
      soll: Math.round(v.soll),
      erledigt: v.ist !== null ? Math.round(v.ist) : null,
      verfuegbar: v.verfuegbar != null ? Math.round(v.verfuegbar) : null,
      offen: v.offen != null ? Math.round(v.offen) : null,
      verspaetet: v.verspaetetSnapshot,
      pickMin: v.pickMin, packMin: v.packMin,
      pickFte: v.pf, packFte: v.af,
      pickIstMin: v.pickIstMin, packIstMin: v.packIstMin,
      pickIstFte: v.pickIstFte, packIstFte: v.packIstFte,
      snapshotZeit: v.snapshotZeit,
      isToday: popupIsToday,
    });
  }

  const handleKwClick = (kw) => { setSelectedKw(kw); setViewMode('tag'); };

  // ── Open segment / GESAMT popup ──
  function openSegPopup(title, titleColor, segKunden, col) {
    const kundenData = segKunden.map((k) => {
      const v = cellVal(k, col);
      let breakdown = null;
      if (col.type === 'tag') {
        const raw = k.days[col.day.dateStr];
        if (raw) {
          breakdown = {
            selberTag: Math.round(Number(raw.pakete_selber_tag) || 0),
            vortagNachCutoff: Math.round(Number(raw.pakete_vortag_nach_cutoff) || 0),
            akkumuliert: Math.round(Number(raw.pakete_akkumuliert) || 0),
          };
        }
      } else {
        const days = col.type === 'kw' ? col.workDays : workDays;
        let st = 0, vnc = 0, akk = 0;
        days.forEach((d) => {
          const raw = k.days[d.dateStr];
          if (raw) {
            st += Math.round(Number(raw.pakete_selber_tag) || 0);
            vnc += Math.round(Number(raw.pakete_vortag_nach_cutoff) || 0);
            akk += Math.round(Number(raw.pakete_akkumuliert) || 0);
          }
        });
        breakdown = { selberTag: st, vortagNachCutoff: vnc, akkumuliert: akk };
      }
      return {
        name: k.name, segment: k.segment || 'B',
        soll: Math.round(v.soll), ist: v.ist !== null ? Math.round(v.ist) : null,
        pickFte: v.pf, packFte: v.af, breakdown,
        verspaetet: v.verspaetet, backlog: v.backlog,
        backlogVerspaetet: v.backlogVerspaetet, puenktlichkeitsrate: v.puenktlichkeitsrate,
      };
    });
    setSegPopup({ title, titleColor, kundenData, colLabel: col.label });
  }

  const needsScroll = columns.length > 8;
  const scrollRef = useRef(null);

  // Auto-scroll to today's column — position today in the left third of visible area
  useEffect(() => {
    if (!scrollRef.current) return;
    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      const el = scrollRef.current.querySelector('[data-today="true"]');
      if (el) {
        const container = scrollRef.current;
        // Scroll so today is at ~20% from left edge of visible area
        const offset = el.offsetLeft - container.offsetLeft - (container.clientWidth * 0.2);
        container.scrollLeft = Math.max(0, offset);
      }
    });
  }, [columns]);

  // ── Empty state ──
  if (kunden.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-[var(--text-secondary)]">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-4 text-[var(--text-tertiary)]"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
        <div className="text-sm font-medium mb-1">{t('forecast.grid.no_data')}</div>
        <div className="text-xs max-w-md text-center">{monatsData.length > 0 ? t('forecast.grid.not_distributed_hint') : t('forecast.grid.create_hint')}</div>
      </div>
    );
  }

  // ── Tooltip helpers ──
  function showGridTooltip(e, data) {
    if (popup) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setGridTooltip({ x: rect.left + rect.width / 2, y: rect.top, ...data });
  }
  function hideGridTooltip() { setGridTooltip(null); }

  // Cell renderer — progressbar design: SOLL + bar + IST/SOLL sub-text
  function renderCell(k, col) {
    const v = cellVal(k, col);
    const isToday = col.type === 'tag' && col.day?.dateStr === today;
    const isPast = col.type === 'tag' && col.day?.dateStr < today;
    const isFuture = col.type === 'tag' && col.day?.dateStr > today;
    const isWe = col.isWeekend;
    const isHol = col.isHoliday && !col.isWeekend;
    const live = isToday ? liveMap[k.id] : null;

    // Fulfillment ratio for progressbar color (IST/SOLL)
    const istVal = live ? Number(live.erledigt) : (v.ist !== null ? v.ist : null);
    const erfRatio = istVal !== null && v.soll > 0 ? istVal / v.soll : null;

    // Progressbar color
    const barColor = erfRatio != null ? (erfRatio >= 0.9 ? '#10B981' : erfRatio >= 0.5 ? '#F59E0B' : '#EF4444') : '#D1D5DB';
    const barPct = erfRatio != null ? Math.min(Math.round(erfRatio * 100), 100) : 0;

    // Cell background — only today gets subtle blue
    let cellBg = undefined;
    if (isToday && v.soll > 0) cellBg = 'rgba(51, 142, 220, 0.04)';

    // Tooltip data — build lines array for display
    const erfuellungPct = v.soll > 0 && istVal !== null ? Math.round((istVal / v.soll) * 100) : null;
    const ttLines = [`${k.name} — ${col.label}`, `SOLL: ${fmt(v.soll)}`];
    if (v.ist !== null) ttLines.push(`${t('forecast.cell.erledigt')}: ${fmt(v.ist)}`);
    if (v.verfuegbar != null) ttLines.push(`${t('forecast.cell.verfuegbar')}: ${fmt(v.verfuegbar)}`);
    if (v.offen != null) ttLines.push(`${t('forecast.cell.offen')}: ${fmt(v.offen)}`);
    if (erfuellungPct !== null) ttLines.push(`${t('forecast.popup.erfuellung')}: ${erfuellungPct}%`);
    if (v.verspaetetSnapshot != null && v.verspaetetSnapshot > 0) ttLines.push(`${t('forecast.popup.verspaetet')}: ${v.verspaetetSnapshot}`);
    if (v.verspaetetSnapshot === null && v.offen != null && v.offen > 0) ttLines.push(`${t('forecast.popup.nochOffen')}: ${fmt(v.offen)}`);
    // Aktionszeitraum tooltip
    const dayAktion = col.type === 'tag' && col.day ? aktionenDayMap.kundeDayMap.get(`${k.id}-${col.day.dateStr}`) : null;
    if (dayAktion) ttLines.push(`\u26A1 ${dayAktion.titel} (${dayAktion.multiplikator ? dayAktion.multiplikator + 'x' : '+' + dayAktion.zusatz_orders})`);
    const tooltipData = { lines: ttLines };

    return (
      <td key={col.key}
        className="text-center cursor-pointer transition-colors hover:bg-surface-hover"
        style={{
          minWidth: (isWe || isHol) ? 50 : (isToday ? 120 : 80),
          padding: '6px 4px',
          fontVariantNumeric: 'tabular-nums',
          borderBottom: '1px solid var(--border-subtle)',
          ...(isWe && !cellBg ? { backgroundColor: 'var(--bg-inset)', opacity: 0.7 } : {}),
          ...(isHol && !cellBg ? { backgroundColor: 'rgba(239, 68, 68, 0.06)', opacity: 0.7 } : {}),
          ...(cellBg ? { backgroundColor: cellBg } : {}),
        }}
        onClick={() => openPopup(k, col)}
        onMouseEnter={(e) => showGridTooltip(e, tooltipData)}
        onMouseLeave={hideGridTooltip}>
        {v.soll > 0 ? (
          <div className="flex flex-col items-center" style={{ lineHeight: 1.3 }}>
            {/* SOLL value */}
            <span style={{ fontSize: 15, fontWeight: 500, color: isFuture ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>{fmt(v.soll)}</span>
            {/* Progressbar — only for past + today */}
            {!isFuture && istVal !== null && (
              <div style={{ height: 4, borderRadius: 2, backgroundColor: 'var(--border-table, #F3F4F6)', marginTop: 6, width: '100%', overflow: 'hidden', minWidth: 40 }}>
                <div style={{ height: '100%', borderRadius: 2, backgroundColor: barColor, width: `${barPct}%`, transition: 'width 0.3s ease' }} />
              </div>
            )}
            {/* Sub-text: past = erledigt / verfügbar, today = offen (pickings_todo) */}
            {isPast && istVal !== null && v.verfuegbar != null && (
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{fmt(Math.round(istVal))} / {fmt(Math.round(v.verfuegbar))}</span>
            )}
            {isToday && istVal !== null && (() => {
              const openCount = v.offen != null ? Math.round(v.offen) : Math.max(0, Math.round(v.soll - istVal));
              const doneCount = fmt(Math.round(istVal));
              return openCount > 0
                ? <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, whiteSpace: 'nowrap' }}>{doneCount} / {fmt(openCount)} {t('forecast.grid.open')}</span>
                : <span style={{ fontSize: 11, color: '#10B981', marginTop: 2, whiteSpace: 'nowrap' }}>{doneCount} &#10003;</span>;
            })()}
          </div>
        ) : v.soll === 0 && !isWe && !isHol ? (
          <span className="text-[var(--text-secondary)]" style={{ fontSize: 13 }}>–</span>
        ) : (isWe || isHol) ? (
          <span className="text-[var(--text-secondary)]" style={{ fontSize: 12 }}>–</span>
        ) : null}
      </td>
    );
  }

  return (
    <>
      {/* KW breadcrumb */}
      {viewMode === 'tag' && selectedKw && (
        <div className="flex items-center gap-2 mb-3 text-sm">
          <button onClick={() => { setSelectedKw(null); setViewMode('woche'); }} className="text-forecast hover:text-forecast/80 transition-colors flex items-center gap-1.5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
            {t('forecast.grid.week_view')}
          </button>
          <span className="text-[var(--text-tertiary)]">/</span>
          <span className="text-content-tertiary font-semibold">KW {selectedKw}</span>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-[11px] text-[var(--text-secondary)] flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 16, height: 4, borderRadius: 2, backgroundColor: '#10B981' }} /> {t('forecast.legend.erledigt90')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 16, height: 4, borderRadius: 2, backgroundColor: '#F59E0B' }} /> {t('forecast.legend.erledigt50')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 16, height: 4, borderRadius: 2, backgroundColor: '#EF4444' }} /> {t('forecast.legend.erledigtLow')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 16, height: 4, borderRadius: 2, backgroundColor: '#338edc' }} /> {t('forecast.legend.todayLive')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span style={{ width: 16, height: 3, borderRadius: 2, backgroundColor: '#EF4444' }} /> {t('forecast.legend.holiday')}
        </span>
        <span style={{ width: 1, height: 14, backgroundColor: 'var(--border-subtle)', margin: '0 2px' }} />
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: '#338edc' }} /> A</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: '#4D9CE0' }} /> B</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-[3px]" style={{ backgroundColor: '#F59E0B' }} /> C</span>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-[14px] mb-4 flex-wrap">
        <div className="relative">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-tertiary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input type="text" value={searchKunde} onChange={e => setSearchKunde(e.target.value)} placeholder={t('forecast.grid.search_customer')} className="pl-8 pr-3 py-[7px] bg-surface-card border border-line rounded-[10px] text-[13px] text-content focus:outline-none focus:border-forecast/50 w-[180px]" style={{ fontFamily: 'inherit' }} />
        </div>
        <select value={filterSegment} onChange={e => setFilterSegment(e.target.value)} className="px-3 py-[6px] bg-surface-card border border-line rounded-[10px] text-[13px] text-content focus:outline-none focus:border-forecast/50 cursor-pointer" style={{ fontFamily: 'inherit' }}>
          <option value="">{t('forecast.grid.all_segments')}</option>
          <option value="A">{t('forecast.grid.a_customers')}</option>
          <option value="B">{t('forecast.grid.b_customers')}</option>
          <option value="C">{t('forecast.grid.c_customers')}</option>
        </select>
        {(searchKunde || filterSegment) && (
          <span className="text-[11px] text-content-tertiary">{filteredKunden.length}/{kunden.length}</span>
        )}
      </div>

      <div ref={scrollRef} onScroll={() => setGridTooltip(null)} className="bg-surface-card rounded-card shadow-card overflow-x-auto mb-5">
        <table className="w-full border-collapse" style={{ minWidth: 1100 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <th className="sticky left-0 z-20 bg-surface-card text-left border-r border-line" style={{ minWidth: 200, maxWidth: 250, fontSize: 10.5, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.3px', padding: '10px 8px 10px 16px' }}>
                {t('forecast.grid.customer_col')}
              </th>
              {columns.map((col) => {
                const isToday = col.type === 'tag' && col.day?.dateStr === today;
                const isWe = col.isWeekend;
                const isHol = col.isHoliday && !col.isWeekend;
                const dayAktionen = col.type === 'tag' && col.day ? aktionenDayMap.dayMap.get(col.day.dateStr) : null;
                const hasAktion = dayAktionen && dayAktionen.length > 0;
                const aktionTyp = hasAktion ? dayAktionen[0].typ : null;
                const typColors = { black_week: '#EF4444', sale: '#F59E0B', launch: '#3B82F6', kampagne: '#8B5CF6', saison: '#06B6D4', sonstige: '#6B7280' };
                const aktionColor = aktionTyp ? (typColors[aktionTyp] || '#6B7280') : null;
                const colTitle = isHol ? `${col.holidayName}` : hasAktion ? dayAktionen.map(a => `${a.kunde_name}: ${a.titel} (${a.multiplikator ? a.multiplikator + 'x' : '+' + a.zusatz_orders})`).join(' | ') : col.type === 'kw' ? t('forecast.grid.click_day_view') : undefined;
                return (
                  <th key={col.key}
                    data-today={isToday || undefined}
                    className={`text-center ${col.type === 'kw' ? 'cursor-pointer hover:bg-surface-hover' : ''} transition-colors`}
                    style={{
                      minWidth: (isWe || isHol) ? 50 : (isToday ? 120 : 85),
                      padding: '10px 8px',
                      fontSize: 10.5,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      color: isToday ? 'white' : (isWe || isHol) ? 'var(--text-muted)' : 'var(--text-tertiary)',
                      whiteSpace: 'nowrap',
                      ...(hasAktion && !isToday ? { borderTop: `3px solid ${aktionColor}` } : {}),
                      ...(isHol && !isToday ? { borderTop: '3px solid #EF4444' } : {}),
                      ...(isToday ? { background: 'linear-gradient(135deg, #6366F1, #4D9CE0)', borderRadius: '8px 8px 0 0', fontWeight: 700, fontSize: 12 } : isHol ? { backgroundColor: 'rgba(239, 68, 68, 0.06)' } : isWe ? { backgroundColor: 'var(--bg-inset)' } : {}),
                    }}
                    title={colTitle}
                    onClick={col.type === 'kw' ? () => handleKwClick(col.kw) : undefined}>
                    <div className="flex items-center justify-center gap-1">
                      {col.label}
                      {isHol && !isToday && <span style={{ fontSize: 8, color: '#EF4444', marginLeft: 2 }}>F</span>}
                      {isToday && (liveData || hasLiveGrid) && (
                        <span style={{ fontSize: 9, fontWeight: 500, color: '#fff', backgroundColor: '#338edc', borderRadius: 4, padding: '1px 5px', lineHeight: '14px', marginLeft: 4 }}>LIVE</span>
                      )}
                    </div>
                    {col.sublabel && <div style={{ fontSize: 10, fontWeight: 400, opacity: isToday ? 0.7 : 1, color: isToday ? 'rgba(255,255,255,0.7)' : 'var(--text-muted)' }}>{col.sublabel}</div>}
                  </th>
                );
              })}
              <th className="sticky right-0 z-20 bg-surface-active text-center border-l-2 border-line" style={{ minWidth: 80, fontSize: 10.5, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.3px', padding: '10px 16px 10px 8px' }}>{t('forecast.grid.total_col')}</th>
            </tr>
          </thead>
          <tbody>
            {segOrder.flatMap((seg) => {
              const segKunden = filteredSegments[seg] || [];
              if (segKunden.length === 0) return [];
              const isCollapsed = collapsedSegs[seg];
              const segMonatTotal = segKunden.reduce((sum, k) => { const mf = monatsData.find((m) => m.kunde_id === k.id); return sum + (mf ? Number(mf.pakete_gesamt) : 0); }, 0);
              const segColor = segColors[seg];
              const rows = [];

              // ── Compact segment header (30px) ──
              rows.push(
                <tr key={`seg-${seg}`} className="border-b border-line cursor-pointer transition-colors" style={{ height: 38 }} onClick={() => toggleSeg(seg)}>
                  <td colSpan={columns.length + 2} style={{ borderLeft: `3px solid ${segColor}`, padding: '0 12px' }}>
                    <div className="flex items-center gap-2">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                        className={`text-[var(--text-secondary)] transition-transform duration-150 ${isCollapsed ? '' : 'rotate-90'}`}>
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                      <span className="uppercase tracking-wide" style={{ fontSize: 13, fontWeight: 700, color: segColor }}>{t('forecast.grid.seg_customers', { seg })}</span>
                      <span className="text-[var(--text-tertiary)] ml-auto font-mono" style={{ fontSize: 11 }}>{segKunden.length} · {fmt(segMonatTotal)}</span>
                    </div>
                  </td>
                </tr>
              );

              // ── Kunde rows ──
              if (!isCollapsed) {
                segKunden.forEach((k) => {
                  const mf = monatsData.find((m) => m.kunde_id === k.id);
                  const monthTotal = mf ? Number(mf.pakete_gesamt) : 0;
                  let kundeLateTotal = 0;
                  workDays.forEach((d) => { const c = k.days[d.dateStr]; if (c) kundeLateTotal += Number(c.verspaetete_pakete) || 0; });
                  rows.push(
                    <tr key={`k-${k.id}`} className="hover:bg-surface-hover transition-colors" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td className="sticky left-0 z-20 bg-surface-card border-r border-line" style={{ borderLeft: `3px solid ${segColor}`, padding: '10px 16px', minWidth: 200, maxWidth: 280 }}>
                        <div className="flex items-center gap-1">
                          {/* Fulfillment dot — avg of past visible days */}
                          {(() => {
                            let istSum = 0, sollSum = 0;
                            const pastDays = columns.filter(c => c.type === 'tag' && c.day?.dateStr < today && !c.isWeekend && !c.isHoliday).slice(-7);
                            pastDays.forEach(c => { const cv = cellVal(k, c); if (cv.ist !== null && cv.soll > 0) { istSum += cv.ist; sollSum += cv.soll; } });
                            if (sollSum === 0) return null;
                            const avgR = istSum / sollSum;
                            const dotC = avgR >= 0.9 ? '#10B981' : avgR >= 0.5 ? '#F59E0B' : '#EF4444';
                            return <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: dotC, flexShrink: 0 }} />;
                          })()}
                          <span
                            className="text-content hover:text-forecast cursor-pointer truncate"
                            style={{ fontSize: 13, fontWeight: 500, maxWidth: 200 }}
                            onClick={(e) => { e.stopPropagation(); navigate(`/kunden/${k.id}`); }}
                            title={`${k.name} – ${t('forecast.grid.open_details')}`}
                          >{k.name}</span>
                          {rampupMap[k.id] && (
                            <span
                              className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-cyan-500/12 text-cyan-400 flex-shrink-0"
                              title={t('forecast.ai.rampup_info', { months: rampupMap[k.id].months_active, orders: rampupMap[k.id].total_orders.toLocaleString(locale) })}
                            >{t('forecast.ai.rampup')}</span>
                          )}
                          {(() => {
                            const showBadges = qualityConfig?.fq_show_grid_badges !== false && qualityConfig?.fq_show_grid_badges !== 'false';
                            const qs = qualityScores[k.id];
                            if (!showBadges || !qs || qs.quality_score == null) return null;
                            const score = qs.quality_score;
                            const ex = qualityConfig?.fq_threshold_excellent || 90;
                            const gd = qualityConfig?.fq_threshold_good || 70;
                            const md = qualityConfig?.fq_threshold_moderate || 50;
                            const clr = score >= ex ? '#10B981' : score >= gd ? '#338edc' : score >= md ? '#F59E0B' : '#EF4444';
                            const fmt2 = qualityConfig?.fq_badge_format || 'score';
                            const lbl = score >= ex ? t('forecastQuality.excellent') : score >= gd ? t('forecastQuality.good') : score >= md ? t('forecastQuality.moderate') : t('forecastQuality.poor');
                            const txt = fmt2 === 'label' ? lbl : fmt2 === 'both' ? `${Math.round(score)} ${lbl}` : Math.round(score);
                            return (
                              <span className="ml-1 flex-shrink-0" style={{ background: `${clr}15`, color: clr, padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, fontFamily: 'monospace', lineHeight: '14px' }}
                                title={`${t('forecastQuality.accuracy')} ${qs.avg_accuracy?.toFixed(0) || '–'}%, ${t('forecastQuality.bias')} ${qs.avg_bias != null ? (qs.avg_bias > 0 ? '+' : '') + qs.avg_bias.toFixed(0) + '%' : '–'}, ${t('forecastQuality.consistency')} ${qs.consistency?.toFixed(0) || '–'}%`}>
                                {txt}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      {columns.map((col) => renderCell(k, col))}
                      <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold text-content border-l-2 border-line" style={{ fontSize: 14, fontWeight: 700, padding: '10px 16px', textAlign: 'right' }}>
                        <div className="flex items-center justify-end gap-1">
                          {fmt(monthTotal)}
                          {kundeLateTotal > 0 && <span style={{ fontSize: 10, fontWeight: 600, color: '#EF4444', backgroundColor: '#FEE2E2', padding: '1px 4px', borderRadius: 4, lineHeight: '14px' }}>{kundeLateTotal}</span>}
                        </div>
                      </td>
                    </tr>
                  );
                });
              }

              // ── Σ Segment summary row ──
              rows.push(
                <tr key={`seg-sum-${seg}`} style={{ borderBottom: '2px solid var(--border-primary)' }}>
                  <td className="sticky left-0 z-20 border-r border-line" style={{ borderLeft: `3px solid ${segColor}`, color: segColor, fontSize: 12, fontWeight: 700, padding: '10px 16px', backgroundColor: 'var(--bg-inset)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                    Σ {seg}
                  </td>
                  {columns.map((col) => {
                    const st = segColTotal(segKunden, col);
                    const isToday = col.type === 'tag' && col.day?.dateStr === today;
                    const isFuture = col.type === 'tag' && col.day?.dateStr > today;
                    const segIst = st.ist != null ? st.ist : null;
                    const segErfR = segIst !== null && st.soll > 0 ? segIst / st.soll : null;
                    const segBarColor = segErfR != null ? (segErfR >= 0.9 ? '#10B981' : segErfR >= 0.5 ? '#F59E0B' : '#EF4444') : '#D1D5DB';
                    const segBarPct = segErfR != null ? Math.min(Math.round(segErfR * 100), 100) : 0;
                    let segCellBg = 'var(--bg-inset)';
                    if (isToday && st.soll > 0) segCellBg = 'rgba(51, 142, 220, 0.04)';
                    return (
                      <td key={col.key}
                        className="text-center cursor-pointer hover:bg-surface-active transition-colors"
                        style={{ padding: '8px 6px', backgroundColor: segCellBg, fontWeight: 600 }}
                        onClick={() => openSegPopup(`Σ ${t('forecast.grid.seg_customers', { seg })}`, segColor, segKunden, col)}>
                        <div className="flex flex-col items-center" style={{ lineHeight: 1.3 }}>
                          <span className="text-content" style={{ fontSize: 15, fontWeight: 700, color: isFuture ? 'var(--text-tertiary)' : undefined }}>{st.soll > 0 ? fmt(st.soll) : ''}</span>
                          {st.soll > 0 && !isFuture && segIst !== null && (
                            <>
                              <div style={{ height: 4, borderRadius: 2, backgroundColor: 'var(--border-table, #F3F4F6)', marginTop: 6, width: '100%', overflow: 'hidden', minWidth: 40 }}>
                                <div style={{ height: '100%', borderRadius: 2, backgroundColor: segBarColor, width: `${segBarPct}%`, transition: 'width 0.3s ease' }} />
                              </div>
                              {(() => {
                                const isPastSeg = col.type === 'tag' && col.day?.dateStr < today;
                                if (isPastSeg && st.verfuegbar != null) {
                                  return <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{fmt(Math.round(segIst))} / {fmt(Math.round(st.verfuegbar))}</span>;
                                }
                                if (isToday) {
                                  const segOpen = st.offen != null ? Math.round(st.offen) : Math.max(0, Math.round(st.soll - segIst));
                                  const segDone = fmt(Math.round(segIst));
                                  return segOpen > 0
                                    ? <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, whiteSpace: 'nowrap' }}>{segDone} / {fmt(segOpen)} {t('forecast.grid.open')}</span>
                                    : <span style={{ fontSize: 11, color: '#10B981', marginTop: 2, whiteSpace: 'nowrap' }}>{segDone} &#10003;</span>;
                                }
                                return null;
                              })()}
                            </>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  {(() => {
                    let segLate = 0;
                    segKunden.forEach((k) => { workDays.forEach((d) => { const c = k.days[d.dateStr]; if (c) segLate += Number(c.verspaetete_pakete) || 0; }); });
                    return (
                      <td className="sticky right-0 z-20 bg-surface-active text-center font-mono border-l-2 border-line" style={{ color: segColor, fontSize: 13, fontWeight: 700, padding: '10px 8px' }}>
                        <div className="flex items-center justify-center gap-1">
                          {fmt(segMonatTotal)}
                          {segLate > 0 && <span style={{ fontSize: 10, fontWeight: 600, color: '#EF4444', backgroundColor: '#FEE2E2', padding: '1px 4px', borderRadius: 4, lineHeight: '14px' }}>{segLate}</span>}
                        </div>
                      </td>
                    );
                  })()}
                </tr>
              );

              return rows;
            })}

            {/* Σ GESAMT */}
            <tr style={{ borderTop: '2px solid var(--border-primary)', borderBottom: '2px solid var(--border-primary)' }}>
              <td className="sticky left-0 z-20 uppercase border-r border-line" style={{ borderLeft: '3px solid #10B981', fontSize: 12, fontWeight: 700, padding: '10px 16px', backgroundColor: 'var(--bg-inset)', color: '#10B981', letterSpacing: '0.3px' }}>Σ {t('forecast.grid.gesamt')}</td>
              {columns.map((col) => {
                const gt = segColTotal(kunden, col);
                const isToday = col.type === 'tag' && col.day?.dateStr === today;
                const isFuture = col.type === 'tag' && col.day?.dateStr > today;
                const gtIst = gt.ist != null ? gt.ist : null;
                const gtErfR = gtIst !== null && gt.soll > 0 ? gtIst / gt.soll : null;
                const gtBarColor = gtErfR != null ? (gtErfR >= 0.9 ? '#10B981' : gtErfR >= 0.5 ? '#F59E0B' : '#EF4444') : '#D1D5DB';
                const gtBarPct = gtErfR != null ? Math.min(Math.round(gtErfR * 100), 100) : 0;
                let gtCellBg = 'var(--bg-inset)';
                if (isToday && gt.soll > 0) gtCellBg = 'rgba(51, 142, 220, 0.04)';
                return (
                  <td key={col.key}
                    className="text-center cursor-pointer hover:bg-surface-active transition-colors"
                    style={{ padding: '8px 6px', backgroundColor: gtCellBg, fontWeight: 600 }}
                    onClick={() => openSegPopup(`Σ ${t('forecast.grid.gesamt')}`, '#10B981', kunden, col)}>
                    <div className="flex flex-col items-center" style={{ lineHeight: 1.3 }}>
                      <span className="text-content" style={{ fontSize: 15, fontWeight: 700, color: isFuture ? 'var(--text-tertiary)' : undefined }}>{gt.soll > 0 ? fmt(gt.soll) : ''}</span>
                      {gt.soll > 0 && !isFuture && gtIst !== null && (
                        <>
                          <div style={{ height: 4, borderRadius: 2, backgroundColor: 'var(--border-table, #F3F4F6)', marginTop: 6, width: '100%', overflow: 'hidden', minWidth: 40 }}>
                            <div style={{ height: '100%', borderRadius: 2, backgroundColor: gtBarColor, width: `${gtBarPct}%`, transition: 'width 0.3s ease' }} />
                          </div>
                          {(() => {
                            const isPastGt = col.type === 'tag' && col.day?.dateStr < today;
                            if (isPastGt && gt.verfuegbar != null) {
                              return <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{fmt(Math.round(gtIst))} / {fmt(Math.round(gt.verfuegbar))}</span>;
                            }
                            if (isToday) {
                              const gtOpen = gt.offen != null ? Math.round(gt.offen) : Math.max(0, Math.round(gt.soll - gtIst));
                              const gtDone = fmt(Math.round(gtIst));
                              return gtOpen > 0
                                ? <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, whiteSpace: 'nowrap' }}>{gtDone} / {fmt(gtOpen)} {t('forecast.grid.open')}</span>
                                : <span style={{ fontSize: 11, color: '#10B981', marginTop: 2, whiteSpace: 'nowrap' }}>{gtDone} &#10003;</span>;
                            }
                            return null;
                          })()}
                        </>
                      )}
                    </div>
                  </td>
                );
              })}
              {(() => {
                let gesamtLate = 0;
                kunden.forEach((k) => { workDays.forEach((d) => { const c = k.days[d.dateStr]; if (c) gesamtLate += Number(c.verspaetete_pakete) || 0; }); });
                return (
                  <td className="sticky right-0 z-20 bg-surface-active text-center font-mono border-l-2 border-line" style={{ fontSize: 14, fontWeight: 700, padding: '10px 16px', color: '#10B981' }}>
                    <div className="flex items-center justify-center gap-1">
                      {fmt(monatsData.reduce((s, m) => s + Number(m.pakete_gesamt), 0))}
                      {gesamtLate > 0 && <span style={{ fontSize: 10, fontWeight: 600, color: '#EF4444', backgroundColor: '#FEE2E2', padding: '1px 4px', borderRadius: 4, lineHeight: '14px' }}>{gesamtLate}</span>}
                    </div>
                  </td>
                );
              })()}
            </tr>

            {/* DEMAND (Auftragseingang) */}
            {(() => {
              const anyDemand = columns.some(col => colDemand(col).hasData);
              if (!anyDemand) return null;
              let totalDemand = 0;
              columns.forEach(col => { totalDemand += colDemand(col).demand; });
              return (
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 600, padding: '6px 16px', color: '#6366F1', letterSpacing: '0.3px' }}>
                    {t('forecast.grid.demand')}
                  </td>
                  {columns.map((col) => {
                    const cd = colDemand(col);
                    if (!cd.hasData) return <td key={col.key} className="text-center text-[var(--text-muted)]" style={{ fontSize: 12, padding: '6px 8px' }}>–</td>;
                    return (
                      <td key={col.key} className="text-center font-mono font-semibold" style={{ fontSize: 12, padding: '6px 8px', color: '#6366F1' }}>
                        {fmt(cd.demand)}
                      </td>
                    );
                  })}
                  <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: '#6366F1' }}>
                    {totalDemand > 0 ? fmt(totalDemand) : '–'}
                  </td>
                </tr>
              );
            })()}

            {/* Pick FTE */}
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 500, padding: '6px 16px', color: 'var(--text-secondary)', letterSpacing: '0.3px' }}>{t('forecast.grid.pick_fte')}</td>
              {columns.map((col) => {
                const fte = colFte(col);
                const v = fteDisplay(fte, col, 'pick');
                return <td key={col.key} className="text-center font-mono" style={{ fontSize: 12, padding: '6px 8px', color: v > 0 ? 'var(--text-secondary)' : 'var(--text-muted)', fontWeight: 500 }}>{v > 0 ? v.toFixed(1) : ''}</td>;
              })}
              <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: 'var(--text-secondary)' }}>{peakDailyFte('benoetigte_pick_fte').toFixed(1)}</td>
            </tr>

            {/* Pack FTE */}
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 500, padding: '6px 16px', color: 'var(--text-secondary)', letterSpacing: '0.3px' }}>{t('forecast.grid.pack_fte')}</td>
              {columns.map((col) => {
                const fte = colFte(col);
                const v = fteDisplay(fte, col, 'pack');
                return <td key={col.key} className="text-center font-mono" style={{ fontSize: 12, padding: '6px 8px', color: v > 0 ? 'var(--text-secondary)' : 'var(--text-muted)', fontWeight: 500 }}>{v > 0 ? v.toFixed(1) : ''}</td>;
              })}
              <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: 'var(--text-secondary)' }}>{peakDailyFte('benoetigte_pack_fte').toFixed(1)}</td>
            </tr>

            {/* Total FTE */}
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 700, padding: '6px 16px', color: 'var(--text-secondary)', letterSpacing: '0.3px' }}>{t('forecast.kpi.total_fte')}</td>
              {columns.map((col) => {
                const fte = colFte(col);
                const v = fteDisplay(fte, col, 'total');
                return <td key={col.key} className="text-center font-mono font-bold" style={{ fontSize: 12, padding: '6px 8px', color: v > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>{v > 0 ? v.toFixed(1) : ''}</td>;
              })}
              <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold text-content border-l-2 border-line" style={{ fontSize: 13, padding: '6px 8px' }}>
                {(() => { let max = 0; workDays.forEach((d) => { let s = 0; kunden.forEach((k) => { const c = k.days[d.dateStr]; if (c) s += (Number(c.benoetigte_pick_fte) || 0) + (Number(c.benoetigte_pack_fte) || 0); }); if (s > max) max = s; }); return max.toFixed(1); })()}
              </td>
            </tr>

            {/* Benchmark FTE-Bedarf */}
            {fteBedarf?.days?.length > 0 && (
              <tr style={{ borderTop: '2px solid var(--border-primary)', borderBottom: '1px solid var(--border-subtle)' }}>
                <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 700, padding: '6px 16px', color: '#EF4444', letterSpacing: '0.3px' }}>
                  {t('forecast.grid.fte_bedarf')}
                </td>
                {columns.map((col) => {
                  const bf = colBenchFte(col);
                  if (!bf) return <td key={col.key} className="text-center text-[var(--text-secondary)]" style={{ fontSize: 12, padding: '6px 8px' }}>–</td>;
                  const color = bf.status === 'red' ? '#EF4444' : bf.status === 'orange' ? '#F59E0B' : '#10B981';
                  return (
                    <td key={col.key} className="text-center font-mono font-bold relative group" style={{ fontSize: 12, padding: '6px 8px', color }}>
                      {bf.bedarf.toFixed(1)}
                      {bf.geplant > 0 && (
                        <div className="text-[9px] font-normal" style={{ color: bf.delta >= 0 ? '#10B981' : '#EF4444' }}>
                          {bf.delta >= 0 ? '+' : ''}{bf.delta.toFixed(1)}
                        </div>
                      )}
                      {bf.topCustomers && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2.5 py-1.5 rounded-xl bg-surface-elevated border border-line shadow-elevated text-[10px] text-left opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap min-w-[180px]">
                          <div className="text-[var(--text-secondary)] mb-1 font-semibold">{t('forecast.grid.top_customers')}</div>
                          {bf.topCustomers.map((c, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 text-content-secondary">
                              <span className="truncate max-w-[120px]">{c.name}</span>
                              <span className="font-mono text-content">{c.fte_anteil.toFixed(1)} FTE</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: '#EF4444' }}>
                  {fteBedarf.kpis ? fteBedarf.kpis.peak_fte_bedarf.toFixed(1) : '–'}
                </td>
              </tr>
            )}

            {/* Geplant FTE (from Schichtplanung) */}
            {fteBedarf?.days?.length > 0 && fteBedarf.days.some(d => d.geplant_fte > 0) && (
              <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 500, padding: '6px 16px', color: '#338edc', letterSpacing: '0.3px' }}>
                  {t('forecast.grid.planned_label')}
                </td>
                {columns.map((col) => {
                  const bf = colBenchFte(col);
                  if (!bf || bf.geplant === 0) return <td key={col.key} className="text-center text-[var(--text-secondary)]" style={{ fontSize: 12, padding: '6px 8px' }}>–</td>;
                  return (
                    <td key={col.key} className="text-center font-mono" style={{ fontSize: 12, padding: '6px 8px', color: '#338edc' }}>
                      {bf.geplant.toFixed(0)}
                    </td>
                  );
                })}
                <td className="sticky right-0 z-20 bg-surface-active text-center font-mono border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: '#338edc' }}>
                  {fteBedarf.kpis ? fteBedarf.kpis.avg_geplant.toFixed(1) : '–'}
                </td>
              </tr>
            )}

            {/* AUFHOL-FTE */}
            {(() => {
              let anyOverdue = false;
              columns.forEach(col => {
                const s = colLateStats(col);
                if (s.late > 0) anyOverdue = true;
              });
              if (!anyOverdue) return null;
              let totalCatchUp = 0;
              return (
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 600, padding: '6px 16px', color: '#F59E0B', letterSpacing: '0.3px' }}>
                    {t('forecast.grid.catchUpFte')}
                  </td>
                  {columns.map((col) => {
                    const s = colLateStats(col);
                    if (!s.hasData || s.late === 0) return <td key={col.key} className="text-center text-[var(--text-muted)]" style={{ fontSize: 12, padding: '6px 8px' }}>–</td>;
                    // avg min per order from FTE data
                    const fte = colFte(col);
                    const colSoll = colPakete(col);
                    const avgMin = colSoll > 0 && (fte.pick + fte.pack) > 0 ? ((fte.pick + fte.pack) * 480) / colSoll : 3;
                    const catchUpFte = (s.late * avgMin) / 480;
                    totalCatchUp += catchUpFte;
                    return (
                      <td key={col.key} className="text-center font-mono font-semibold" style={{ fontSize: 12, padding: '6px 8px', color: '#F59E0B' }}>
                        +{catchUpFte.toFixed(1)}
                      </td>
                    );
                  })}
                  <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: '#F59E0B' }}>
                    {totalCatchUp > 0 ? `+${totalCatchUp.toFixed(1)}` : '–'}
                  </td>
                </tr>
              );
            })()}

            {/* ── Late / Cut-Off Rows ── */}
            {(() => {
              const totalStats = { late: 0, istGesamt: 0, cutoffDone: 0, cutoffTotal: 0 };
              columns.forEach((col) => {
                const s = colLateStats(col);
                totalStats.late += s.late;
                totalStats.istGesamt += s.istGesamt;
                totalStats.cutoffDone += s.cutoffDone;
                totalStats.cutoffTotal += s.cutoffTotal;
              });
              const totalLatePct = totalStats.istGesamt > 0 ? (totalStats.late / totalStats.istGesamt) * 100 : 0;
              const totalCutoffPct = totalStats.cutoffTotal > 0 ? (totalStats.cutoffDone / totalStats.cutoffTotal) * 100 : 0;

              return (
                <>
                  {/* LATE (absolute) */}
                  <tr style={{ borderTop: '2px solid var(--border-primary)', borderBottom: '1px solid var(--border-subtle)' }}>
                    <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 600, padding: '6px 16px', color: '#F59E0B', letterSpacing: '0.3px' }}>
                      {t('forecast.grid.late')}
                    </td>
                    {columns.map((col) => {
                      const s = colLateStats(col);
                      if (!s.hasData) return <td key={col.key} className="text-center text-[var(--text-muted)]" style={{ fontSize: 12, padding: '6px 8px' }}>–</td>;
                      // Color: amber if demand > SOLL (customer excess), red otherwise
                      const cd = colDemand(col);
                      const colSoll = colPakete(col);
                      const lateClr = s.late > 0 ? (cd.hasData && cd.demand > colSoll ? '#F59E0B' : lateColor(s.latePct)) : 'var(--text-muted)';
                      return (
                        <td key={col.key} className="text-center font-mono font-semibold relative group" style={{ fontSize: 12, padding: '6px 8px', color: lateClr }}>
                          {s.late > 0 ? fmt(s.late) : '0'}
                          {s.proKunde.length > 0 && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2.5 py-1.5 rounded-xl bg-surface-elevated border border-line shadow-elevated text-[10px] text-left opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap min-w-[180px]">
                              <div className="text-[var(--text-secondary)] mb-1 font-semibold">{t('forecast.grid.late_packages_label')}</div>
                              {s.proKunde.slice(0, 8).map((c, i) => (
                                <div key={i} className="flex items-center justify-between gap-2 text-content-secondary">
                                  <span className="truncate max-w-[120px]">{c.name}</span>
                                  <span className="font-mono text-content">{fmt(c.late)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: totalStats.late > 0 ? lateColor(totalLatePct) : 'var(--text-muted)' }}>
                      {totalStats.istGesamt > 0 ? fmt(totalStats.late) : '–'}
                    </td>
                  </tr>

                  {/* LATE % */}
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 500, padding: '6px 16px', color: '#F59E0B', letterSpacing: '0.3px' }}>
                      {t('forecast.grid.late_pct')}
                    </td>
                    {columns.map((col) => {
                      const s = colLateStats(col);
                      if (!s.hasData) return <td key={col.key} className="text-center text-[var(--text-muted)]" style={{ fontSize: 12, padding: '6px 8px' }}>–</td>;
                      return (
                        <td key={col.key} className="text-center font-mono" style={{ fontSize: 12, padding: '6px 8px', color: lateColor(s.latePct), fontWeight: 500 }}>
                          {s.latePct.toFixed(1)}%
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: totalStats.istGesamt > 0 ? lateColor(totalLatePct) : 'var(--text-muted)' }}>
                      {totalStats.istGesamt > 0 ? `${totalLatePct.toFixed(1)}%` : '–'}
                    </td>
                  </tr>

                  {/* CUTOFF QUOTE */}
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 500, padding: '6px 16px', color: 'var(--text-primary)', letterSpacing: '0.3px' }}>
                      {t('forecast.grid.cutoff_quote')}
                    </td>
                    {columns.map((col) => {
                      const s = colLateStats(col);
                      if (!s.hasData || s.cutoffTotal === 0) return <td key={col.key} className="text-center text-[var(--text-muted)]" style={{ fontSize: 12, padding: '6px 8px' }}>–</td>;
                      const pct = (s.cutoffDone / s.cutoffTotal) * 100;
                      const color = pct >= 90 ? '#10B981' : pct >= 80 ? '#F59E0B' : '#EF4444';
                      return (
                        <td key={col.key} className="text-center font-mono" style={{ fontSize: 12, padding: '6px 8px', color, fontWeight: 500 }}>
                          {s.cutoffDone}/{s.cutoffTotal}
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: totalStats.cutoffTotal > 0 ? (totalCutoffPct >= 90 ? '#10B981' : totalCutoffPct >= 80 ? '#F59E0B' : '#EF4444') : 'var(--text-muted)' }}>
                      {totalStats.cutoffTotal > 0 ? `${totalStats.cutoffDone}/${totalStats.cutoffTotal}` : '–'}
                    </td>
                  </tr>

                  {/* FULFILLMENT RATE (IST / Demand) */}
                  {(() => {
                    const anyDemand = columns.some(col => colDemand(col).hasData);
                    if (!anyDemand) return null;
                    let totalIst = 0, totalDemand = 0;
                    columns.forEach(col => { const s = colLateStats(col); totalIst += s.istGesamt; totalDemand += colDemand(col).demand; });
                    const totalFr = totalDemand > 0 ? (totalIst / totalDemand) * 100 : 0;
                    const totalFrColor = totalFr >= 95 ? '#10B981' : totalFr >= 85 ? '#F59E0B' : '#EF4444';
                    return (
                      <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td className="sticky left-0 z-20 bg-surface-card uppercase border-r border-line" style={{ fontSize: 11, fontWeight: 600, padding: '6px 16px', color: '#338edc', letterSpacing: '0.3px' }}>
                          {t('forecast.grid.fulfillmentRate')}
                        </td>
                        {columns.map((col) => {
                          const s = colLateStats(col);
                          const cd = colDemand(col);
                          if (!s.hasData || !cd.hasData || cd.demand === 0) return <td key={col.key} className="text-center text-[var(--text-muted)]" style={{ fontSize: 12, padding: '6px 8px' }}>–</td>;
                          const fr = (s.istGesamt / cd.demand) * 100;
                          const frColor = fr >= 95 ? '#10B981' : fr >= 85 ? '#F59E0B' : '#EF4444';
                          return (
                            <td key={col.key} className="text-center font-mono font-semibold" style={{ fontSize: 12, padding: '6px 8px', color: frColor }}>
                              {fr.toFixed(1)}%
                            </td>
                          );
                        })}
                        <td className="sticky right-0 z-20 bg-surface-active text-center font-mono font-bold border-l-2 border-line" style={{ fontSize: 12, padding: '6px 8px', color: totalDemand > 0 ? totalFrColor : 'var(--text-muted)' }}>
                          {totalDemand > 0 ? `${totalFr.toFixed(1)}%` : '–'}
                        </td>
                      </tr>
                    );
                  })()}
                </>
              );
            })()}
          </tbody>
        </table>
      </div>

      {/* Detail Popup */}
      {popup && (
        <DetailPopup
          kunde={popup.kunde} colLabel={popup.colLabel} soll={popup.soll}
          erledigt={popup.erledigt} verfuegbar={popup.verfuegbar} offen={popup.offen}
          verspaetet={popup.verspaetet} pickMin={popup.pickMin} packMin={popup.packMin}
          pickFte={popup.pickFte} packFte={popup.packFte}
          pickIstMin={popup.pickIstMin} packIstMin={popup.packIstMin}
          pickIstFte={popup.pickIstFte} packIstFte={popup.packIstFte}
          snapshotZeit={popup.snapshotZeit} isToday={popup.isToday}
          onClose={() => setPopup(null)}
          t={t} locale={locale}
        />
      )}

      {/* Summary Popup */}
      {segPopup && (
        <SummaryPopup
          title={segPopup.title} titleColor={segPopup.titleColor}
          kundenData={segPopup.kundenData} colLabel={segPopup.colLabel}
          onClose={() => setSegPopup(null)}
          t={t} locale={locale}
        />
      )}

      {/* Custom Grid Tooltip */}
      {gridTooltip && (
        <div
          className="rounded-lg px-3 py-2 text-xs shadow-sm"
          style={{
            position: 'fixed',
            top: gridTooltip.y + 8,
            left: gridTooltip.x,
            zIndex: 9999,
            pointerEvents: 'none',
            backgroundColor: '#1A1A2E',
            color: '#fff',
            maxWidth: 220,
          }}
        >
          {gridTooltip.lines.map((ln, i) => (
            <div key={i} className={i === 0 ? 'font-semibold mb-0.5' : 'opacity-80'}>{ln}</div>
          ))}
        </div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FORECAST WIZARD (4 Steps) – Enhanced
// ═══════════════════════════════════════════════════════════════════

const presets = {
  standard: { labelKey: 'forecast.wizard.preset.standard', values: { montag: 1.0, dienstag: 1.0, mittwoch: 1.0, donnerstag: 1.0, freitag: 0.8, samstag: 0.8, sonntag: 1.1 } },
  equal: { labelKey: 'forecast.wizard.preset.equal', values: { montag: 1.0, dienstag: 1.0, mittwoch: 1.0, donnerstag: 1.0, freitag: 1.0, samstag: 0.5, sonntag: 0.3 } },
  monday: { labelKey: 'forecast.wizard.preset.monday_heavy', values: { montag: 1.5, dienstag: 1.0, mittwoch: 1.0, donnerstag: 0.8, freitag: 0.6, samstag: 0.3, sonntag: 0.2 } },
  friday: { labelKey: 'forecast.wizard.preset.friday_light', values: { montag: 1.1, dienstag: 1.0, mittwoch: 1.0, donnerstag: 0.9, freitag: 0.5, samstag: 0.2, sonntag: 0.1 } },
  midweek: { labelKey: 'forecast.wizard.preset.midweek_heavy', values: { montag: 0.9, dienstag: 1.1, mittwoch: 1.2, donnerstag: 1.1, freitag: 0.7, samstag: 0.3, sonntag: 0.2 } },
  noWeekend: { labelKey: 'forecast.wizard.preset.no_weekend', values: { montag: 1.2, dienstag: 1.0, mittwoch: 1.0, donnerstag: 0.9, freitag: 0.7, samstag: 0.0, sonntag: 0.0 } },
};

const weekdayKeys = ['montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag', 'sonntag'];
const weekdayLabelKeys = { montag: 'common.weekdays_short.montag', dienstag: 'common.weekdays_short.dienstag', mittwoch: 'common.weekdays_short.mittwoch', donnerstag: 'common.weekdays_short.donnerstag', freitag: 'common.weekdays_short.freitag', samstag: 'common.weekdays_short.samstag', sonntag: 'common.weekdays_short.sonntag' };

const stepLabelKeys = ['forecast.wizard.step1', 'forecast.wizard.step2', 'forecast.wizard.step3', 'forecast.wizard.step4', 'forecast.wizard.step5'];

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function ForecastWizard({ open, onClose, onComplete, year, month, rampupMap = {}, t, locale, tMonth, initialKundeId = '', initialYear = null, initialMonth = null }) {
  const [step, setStep] = useState(1);
  const [kunden, setKunden] = useState([]);
  const [kundeId, setKundeId] = useState('');
  const [wizYear, setWizYear] = useState(year);
  const [wizMonth, setWizMonth] = useState(month < 12 ? month + 1 : 1);
  const [pakete, setPakete] = useState('');
  const [weights, setWeights] = useState({ montag: 1.0, dienstag: 1.0, mittwoch: 1.0, donnerstag: 1.0, freitag: 0.8, samstag: 0.8, sonntag: 1.1 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [feiertage, setFeiertage] = useState([]);
  const [existingForecasts, setExistingForecasts] = useState([]);
  const [kundenSearch, setKundenSearch] = useState('');
  // Forecast v2: SOP-aware fields per customer
  const [avgSkus, setAvgSkus] = useState('');
  const [avgArticles, setAvgArticles] = useState('');
  const [sopAnteil, setSopAnteil] = useState('');
  const [histAvgSkus, setHistAvgSkus] = useState(null);
  const [histAvgArticles, setHistAvgArticles] = useState(null);
  const [histSopAnteil, setHistSopAnteil] = useState(null);
  const [histSopPick, setHistSopPick] = useState(0);
  const [histSopPack, setHistSopPack] = useState(0);
  const [histNormalPick, setHistNormalPick] = useState(null);
  const [histNormalPack, setHistNormalPack] = useState(null);
  const [histSampleSize, setHistSampleSize] = useState(0);
  const [loadingProfil, setLoadingProfil] = useState(false);
  const [aiRampupSuggestion, setAiRampupSuggestion] = useState(null);
  const [loadingAiSuggest, setLoadingAiSuggest] = useState(false);

  // Load kunden, feiertage, existing forecasts on open
  useEffect(() => {
    if (open) {
      api('/api/kunden').then(setKunden).catch(() => {});
      setStep(1);
      setKundeId(initialKundeId || '');
      setPakete('');
      setWeights({ montag: 1.0, dienstag: 1.0, mittwoch: 1.0, donnerstag: 1.0, freitag: 0.8, samstag: 0.8, sonntag: 1.1 });
      setResult(null);
      setKundenSearch('');
      setWizError(null);
      setAiRampupSuggestion(null);
      setAvgSkus(''); setAvgArticles(''); setSopAnteil('');
      setHistAvgSkus(null); setHistAvgArticles(null); setHistSopAnteil(null);
      if (initialYear && initialMonth) {
        // #1635: Deep-Link aus dem Cockpit — Zielmonat vorbelegen
        setWizMonth(initialMonth);
        setWizYear(initialYear);
      } else {
        const nextMonth = month < 12 ? month + 1 : 1;
        const nextYear = month < 12 ? year : year + 1;
        setWizMonth(nextMonth);
        setWizYear(nextYear);
      }
    }
  }, [open, year, month, initialKundeId, initialYear, initialMonth]);

  // Load feiertage + existing forecasts when year/month changes
  useEffect(() => {
    if (open) {
      api(`/api/feiertage?jahr=${wizYear}`).then(setFeiertage).catch(() => {});
      api(`/api/forecast/monats?jahr=${wizYear}&monat=${wizMonth}`).then(setExistingForecasts).catch(() => {});
    }
  }, [open, wizYear, wizMonth]);

  // Load gewichtung + order-profil when kunde selected
  useEffect(() => {
    if (kundeId) {
      api(`/api/wochentags-gewichtung?kunde_id=${kundeId}`).then((data) => {
        if (data && data.montag != null) setWeights({ montag: Number(data.montag) || 1, dienstag: Number(data.dienstag) || 1, mittwoch: Number(data.mittwoch) || 1, donnerstag: Number(data.donnerstag) || 1, freitag: Number(data.freitag) || 1, samstag: Number(data.samstag) ?? 0.3, sonntag: Number(data.sonntag) ?? 0.2 });
      }).catch(() => {});
      // Load order profile for SOP-aware calculations
      setLoadingProfil(true);
      api(`/api/kunden/${kundeId}/order-profil`).then((profil) => {
        if (profil && !profil.error) {
          setHistAvgSkus(profil.avg_skus);
          setHistAvgArticles(profil.avg_articles);
          setHistSopAnteil(profil.sop_anteil);
          setHistSopPick(profil.sop_pick_zeit || 0);
          setHistSopPack(profil.sop_pack_zeit || 0);
          setHistNormalPick(profil.normal_pick_zeit);
          setHistNormalPack(profil.normal_pack_zeit);
          setHistSampleSize(profil.sample_size || 0);
          // Set defaults from historical data
          setAvgSkus(profil.avg_skus != null ? String(profil.avg_skus) : '1');
          setAvgArticles(profil.avg_articles != null ? String(profil.avg_articles) : '1');
          setSopAnteil(profil.sop_anteil != null ? String(Math.round(profil.sop_anteil * 100)) : '0');
        }
      }).catch(() => {}).finally(() => setLoadingProfil(false));
    }
  }, [kundeId]);

  // Load AI suggestion for ramp-up customers
  useEffect(() => {
    setAiRampupSuggestion(null);
    if (!kundeId || !rampupMap[Number(kundeId)]) return;
    const targetMonth = `${wizYear}-${String(wizMonth).padStart(2, '0')}`;
    setLoadingAiSuggest(true);
    api(`/api/forecast/ai-suggest/${kundeId}`, {
      method: 'POST',
      body: { target_month: targetMonth },
    }).then(data => {
      setAiRampupSuggestion(data);
      // Auto-fill if no historical suggestion and no existing forecast
      if (data?.suggested_volume > 0 && !pakete) {
        setPakete(String(data.suggested_volume));
      }
    }).catch(() => {}).finally(() => setLoadingAiSuggest(false));
  }, [kundeId, wizYear, wizMonth, rampupMap]);

  const selectedKunde = kunden.find((k) => k.id === Number(kundeId));

  // Build feiertag set for the selected month
  const feiertagSet = useMemo(() => {
    const s = new Set();
    for (const f of feiertage) {
      const ds = typeof f.datum === 'string' ? f.datum.slice(0, 10) : new Date(f.datum).toLocaleDateString('sv-SE');
      s.add(ds);
    }
    return s;
  }, [feiertage]);

  // Check if forecast already exists for selected kunde+month
  const existingForecast = useMemo(() => {
    if (!kundeId) return null;
    return existingForecasts.find((f) => f.kunde_id === Number(kundeId));
  }, [existingForecasts, kundeId]);

  // Historical suggestion: average of last 3 same-month forecasts
  const historicalSuggestion = useMemo(() => {
    if (!kundeId) return null;
    // We'd need historical data — for now simulate based on existing forecasts
    const existing = existingForecasts.find((f) => f.kunde_id === Number(kundeId));
    if (existing) return Math.round(Number(existing.pakete_gesamt) * (0.95 + Math.random() * 0.1));
    return null;
  }, [kundeId, existingForecasts]);

  // Feiertage-aware arbeitstage calculation
  const arbeitstageInfo = useMemo(() => {
    const dim = new Date(wizYear, wizMonth, 0).getDate();
    let arbeitstage = 0;
    let feiertageImMonat = 0;
    let spitzentage = 0;
    for (let d = 1; d <= dim; d++) {
      const date = new Date(wizYear, wizMonth - 1, d);
      const jsDay = date.getDay();
      const isWeekend = jsDay === 0 || jsDay === 6;
      const ds = isoDate(date);
      const isFeiertag = feiertagSet.has(ds);
      if (isFeiertag && !isWeekend) feiertageImMonat++;
      if (!isWeekend && !isFeiertag) {
        arbeitstage++;
        // Spitzentag check
        if (d > 1) {
          const prev = new Date(wizYear, wizMonth - 1, d - 1);
          const prevWe = prev.getDay() === 0 || prev.getDay() === 6;
          const prevFt = feiertagSet.has(isoDate(prev));
          if (prevWe || prevFt) spitzentage++;
        }
      }
    }
    return { arbeitstage, feiertageImMonat, spitzentage, tageGesamt: dim };
  }, [wizYear, wizMonth, feiertagSet]);

  // Preview with Versandlast-Berechnung (Import-Gewichtung + Cut-Off)
  const preview = useMemo(() => {
    if (!pakete || !selectedKunde) return null;
    const dim = new Date(wizYear, wizMonth, 0).getDate();
    const jsDayToKey = ['sonntag', 'montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag'];

    // Cut-Off-Anteile berechnen (linear über 24h)
    const cutoffStunde = Number(selectedKunde.cutoff_stunde) || 14;
    const vorCutoff = cutoffStunde / 24;
    const nachCutoff = 1 - vorCutoff;

    // Alle Tage im Monat generieren
    const allDays = [];
    for (let d = 1; d <= dim; d++) {
      const date = new Date(wizYear, wizMonth - 1, d);
      const jsDay = date.getDay();
      const isWeekend = jsDay === 0 || jsDay === 6;
      const isFeiertag = feiertagSet.has(isoDate(date));
      const istArbeitstag = !isWeekend && !isFeiertag;
      const dayKey = jsDayToKey[jsDay];
      const importGewicht = weights[dayKey] ?? 0;
      allDays.push({ date, jsDay, isWeekend, isFeiertag, istArbeitstag, dayKey, importGewicht, d });
    }

    // Versandlast pro Arbeitstag berechnen:
    // Jeder Arbeitstag bekommt: eigener Import * vorCutoff + akkumulierte Imports von vorherigen Nicht-Arbeitstagen + vorheriger Arbeitstag * nachCutoff
    const dailyPkts = new Array(dim).fill(0);
    let arbeitstage = 0;

    for (let i = 0; i < allDays.length; i++) {
      const day = allDays[i];
      if (!day.istArbeitstag) continue;
      arbeitstage++;

      let versandlast = day.importGewicht * vorCutoff; // Eigener Tag vor Cutoff

      // Rückwärts: alle Nicht-Arbeitstage → komplett akkumulieren, + vorheriger Arbeitstag nachCutoff
      for (let j = i - 1; j >= 0; j--) {
        const prev = allDays[j];
        if (!prev.istArbeitstag) {
          versandlast += prev.importGewicht; // Wochenende/Feiertag: kompletter Import
        } else {
          versandlast += prev.importGewicht * nachCutoff; // Vorheriger Arbeitstag: nach Cutoff
          break;
        }
      }

      // Sonderfall: Erster Arbeitstag im Monat — Vormonat-Akkumulation nicht verfügbar,
      // aber Fr(nach)+Sa+So des Vormonats approximieren wir über die letzte Woche
      if (i === allDays.findIndex(d => d.istArbeitstag)) {
        // Für den ersten Arbeitstag schauen wir ob wir im Rückwärts-Walk keinen prev Arbeitstag gefunden haben
        let foundPrevArbeitstag = false;
        for (let j = i - 1; j >= 0; j--) {
          if (allDays[j].istArbeitstag) { foundPrevArbeitstag = true; break; }
        }
        if (!foundPrevArbeitstag && i > 0) {
          // Keinen Arbeitstag davor im Monat → letzen Freitag-Äquivalent schätzen
          // Wir nehmen die Freitag-Gewichtung * nachCutoff als Schätzung
          versandlast += (weights.freitag ?? 0.7) * nachCutoff;
        }
      }

      dailyPkts[i] = versandlast;
    }

    // Normalisieren: Versandlast → Pakete
    const totalVersandlast = dailyPkts.reduce((s, v) => s + v, 0);
    const p = Number(pakete);
    for (let i = 0; i < dim; i++) {
      if (totalVersandlast > 0 && dailyPkts[i] > 0) {
        dailyPkts[i] = Math.round(p * dailyPkts[i] / totalVersandlast);
      }
    }

    // Rundungskorrektur
    const sumDist = dailyPkts.reduce((s, v) => s + v, 0);
    const diff = p - sumDist;
    if (diff !== 0) {
      // Letzten Arbeitstag anpassen
      for (let i = dim - 1; i >= 0; i--) {
        if (dailyPkts[i] > 0) { dailyPkts[i] += diff; break; }
      }
    }

    // SOP-weighted time calculation (v2)
    const sa = Number(sopAnteil) / 100 || 0;
    const aSkus = Number(avgSkus) || 1;
    const aArts = Number(avgArticles) || 1;
    const sPick = histSopPick || 0;
    const sPack = histSopPack || 0;
    let nPick, nPack;
    if (selectedKunde.pick_alpha != null && selectedKunde.pick_alpha !== '') {
      nPick = Number(selectedKunde.pick_alpha) + Number(selectedKunde.pick_beta_sku) * aSkus + Number(selectedKunde.pick_beta_artikel) * aArts;
    } else {
      nPick = Number(selectedKunde.picking_time_per_paket) || 2.0;
    }
    if (selectedKunde.pack_alpha != null && selectedKunde.pack_alpha !== '') {
      nPack = Number(selectedKunde.pack_alpha) + Number(selectedKunde.pack_beta_sku) * aSkus + Number(selectedKunde.pack_beta_artikel) * aArts;
    } else {
      nPack = Number(selectedKunde.packing_time_per_paket) || 3.0;
    }
    const pickProAuftrag = (sa > 0 && sPick > 0) ? sa * sPick + (1 - sa) * nPick : nPick;
    const packProAuftrag = (sa > 0 && sPack > 0) ? sa * sPack + (1 - sa) * nPack : nPack;

    const pickMinGesamt = p * pickProAuftrag;
    const packMinGesamt = p * packProAuftrag;
    const pickHGesamt = pickMinGesamt / 60;
    const packHGesamt = packMinGesamt / 60;
    const pickFte = pickHGesamt / (arbeitstage * 7.5);
    const packFte = packHGesamt / (arbeitstage * 7.5);
    const pickOhneSop = p * nPick / 60;
    const packOhneSop = p * nPack / 60;

    return {
      dailyPkts,
      maxPkt: Math.max(...dailyPkts, 1),
      arbeitstage,
      avgPerDay: arbeitstage > 0 ? Math.round(p / arbeitstage) : 0,
      cutoffStunde: cutoffStunde,
      pickMinGesamt, packMinGesamt,
      pickHGesamt, packHGesamt,
      pickFte, packFte,
      pickProAuftrag, packProAuftrag,
      normalPick: nPick, normalPack: nPack,
      sopPick: sPick, sopPack: sPack,
      sopAnteilDecimal: sa,
      pickOhneSop, packOhneSop,
    };
  }, [pakete, weights, wizYear, wizMonth, selectedKunde, feiertagSet, avgSkus, avgArticles, sopAnteil, histSopPick, histSopPack]);

  const [saving, setSaving] = useState(false);
  const [wizError, setWizError] = useState(null);

  // Helper: save forecast_kunden_parameter for this month
  const saveForecastParams = async () => {
    const sa = Number(sopAnteil) / 100 || 0;
    const aSkus = Number(avgSkus) || null;
    const aArts = Number(avgArticles) || null;
    await api('/api/forecast/kunden-parameter', {
      method: 'PUT',
      body: {
        jahr: wizYear,
        monat: wizMonth,
        kunden: [{
          kunde_id: Number(kundeId),
          pakete: Number(pakete) || 0,
          avg_skus: aSkus,
          avg_articles: aArts,
          sop_anteil: sa,
          avg_skus_quelle: histAvgSkus != null && Math.abs(Number(avgSkus) - histAvgSkus) < 0.05 ? 'historisch' : 'manuell',
          avg_articles_quelle: histAvgArticles != null && Math.abs(Number(avgArticles) - histAvgArticles) < 0.05 ? 'historisch' : 'manuell',
          sop_anteil_quelle: histSopAnteil != null && Math.abs(sa - histSopAnteil) < 0.01 ? 'historisch' : 'manuell',
          pick_zeit_berechnet: preview?.pickProAuftrag ?? null,
          pack_zeit_berechnet: preview?.packProAuftrag ?? null,
          sop_pick_zeit: histSopPick || null,
          sop_pack_zeit: histSopPack || null,
          normal_pick_zeit: preview?.normalPick ?? null,
          normal_pack_zeit: preview?.normalPack ?? null,
        }],
      },
    });
  };

  const doSaveEntwurf = async () => {
    setSaving(true);
    setWizError(null);
    try {
      await api('/api/wochentags-gewichtung', { method: 'PUT', body: { kunde_id: Number(kundeId), ...weights } });
      await saveForecastParams();
      await api('/api/forecast/monats', { method: 'POST', body: { kunde_id: Number(kundeId), standort_id: selectedKunde.standort_id, jahr: wizYear, monat: wizMonth, pakete_gesamt: Number(pakete) } });
      onClose(); onComplete();
    } catch (e) {
      console.error(e);
      setWizError(t('forecast.wizard.save_error', { msg: e.message }));
    }
    setSaving(false);
  };

  const doVerteilen = async () => {
    setLoading(true);
    setWizError(null);
    try {
      await api('/api/wochentags-gewichtung', { method: 'PUT', body: { kunde_id: Number(kundeId), ...weights } });
      await saveForecastParams();
      const mf = await api('/api/forecast/monats', { method: 'POST', body: { kunde_id: Number(kundeId), standort_id: selectedKunde.standort_id, jahr: wizYear, monat: wizMonth, pakete_gesamt: Number(pakete) } });
      if (!mf || !mf.id) throw new Error('Monats-Forecast konnte nicht erstellt werden');
      const res = await api(`/api/forecast/monats/${mf.id}/verteilen`, { method: 'POST' });
      if (!res || !res.monats_forecast) throw new Error('Verteilung fehlgeschlagen');
      setResult(res); setStep(6);
    } catch (e) {
      console.error(e);
      setWizError(t('forecast.wizard.error', { msg: e.message }));
    }
    setLoading(false);
  };

  if (!open) return null;

  // Filtered kunden for step 1
  const filteredKunden = kundenSearch
    ? kunden.filter((k) => k.name.toLowerCase().includes(kundenSearch.toLowerCase()))
    : kunden;

  return (
    <Modal open={open} onClose={onClose} title={step <= 5 ? t('forecast.wizard.step_title', { step: t(stepLabelKeys[step - 1]) }) : t('forecast.wizard.result_title')} size="xl">
      {/* Step indicator */}
      {step <= 5 && step < 6 && (
        <div className="flex items-center gap-1 mb-6">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex items-center gap-1">
              <button
                onClick={() => { if (s < step) setStep(s); }}
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold transition-all
                  ${step === s ? 'bg-forecast/25 text-forecast ring-2 ring-forecast/30' : step > s ? 'bg-forecast/15 text-forecast cursor-pointer hover:bg-forecast/20' : 'bg-surface-inset text-[var(--text-tertiary)]'}`}
              >{s}</button>
              {s < 5 && <div className={`w-4 h-0.5 rounded ${step > s ? 'bg-forecast/30' : 'bg-surface-inset'}`} />}
              {s < 5 && <span className={`text-[9px] mr-0.5 hidden lg:inline ${step >= s ? 'text-content-tertiary' : 'text-[var(--text-tertiary)]'}`}>{t(stepLabelKeys[s - 1])}</span>}
              {s < 5 && <div className={`w-2 h-0.5 rounded hidden lg:block ${step > s ? 'bg-forecast/30' : 'bg-surface-inset'}`} />}
            </div>
          ))}
        </div>
      )}

      {/* ═══ STEP 1: KUNDE & MONAT ═══ */}
      {step === 1 && (
        <div className="space-y-5">
          {/* Month/Year selector */}
          <div className="flex gap-3">
            <label className="flex-1 block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('common.month')}</span>
              <select value={wizMonth} onChange={(e) => setWizMonth(Number(e.target.value))} className="mt-1.5 w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2.5 text-sm text-content focus:outline-none focus:border-forecast/40">
                {Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1}>{tMonth(i)}</option>)}
              </select>
            </label>
            <label className="w-28 block">
              <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('common.year')}</span>
              <input type="number" value={wizYear} onChange={(e) => setWizYear(Number(e.target.value))} min="2024" max="2030" className="mt-1.5 w-full bg-surface-inset border border-line-hover rounded-lg px-3 py-2.5 text-sm text-content font-mono focus:outline-none focus:border-forecast/40" />
            </label>
          </div>

          {/* Month quick stats */}
          <div className="flex gap-2">
            <div className="flex-1 bg-surface-card border border-line rounded-lg px-3 py-2 text-center shadow-card">
              <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.working_days_label')}</div>
              <div className="font-mono text-lg font-bold text-content">{arbeitstageInfo.arbeitstage}</div>
            </div>
            <div className="flex-1 bg-surface-card border border-line rounded-lg px-3 py-2 text-center shadow-card">
              <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.legend_holiday')}</div>
              <div className="font-mono text-lg font-bold text-red-400">{arbeitstageInfo.feiertageImMonat}</div>
            </div>
            <div className="flex-1 bg-surface-card border border-line rounded-lg px-3 py-2 text-center shadow-card">
              <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.peak_label')}</div>
              <div className="font-mono text-lg font-bold text-amber-400">{arbeitstageInfo.spitzentage}</div>
            </div>
          </div>

          {/* Kunde search */}
          <div>
            <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('forecast.wizard.customer_label')}</span>
            <div className="relative mt-1.5 mb-3">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input
                type="text"
                value={kundenSearch}
                onChange={(e) => setKundenSearch(e.target.value)}
                placeholder={t('forecast.grid.search_customer')}
                className="w-full bg-surface-inset border border-line-hover rounded-lg pl-9 pr-3 py-2 text-sm text-content placeholder-gray-600 focus:outline-none focus:border-forecast/40"
              />
            </div>

            {/* Kunden cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
              {filteredKunden.map((k) => {
                const isSelected = Number(kundeId) === k.id;
                const hasExisting = existingForecasts.some((f) => f.kunde_id === k.id);
                return (
                  <button
                    key={k.id}
                    onClick={() => setKundeId(String(k.id))}
                    className={`text-left p-3 rounded-card border transition-all duration-150 ${
                      isSelected
                        ? 'bg-forecast/10 border-forecast/30 ring-1 ring-forecast/20'
                        : 'bg-surface-card border-line hover:bg-surface-hover hover:border-line-hover'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-2 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: segColors[k.segment] || '#666' }} />
                      <span className="text-sm font-semibold text-content truncate">{k.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-[var(--text-secondary)]">Seg {k.segment}</span>
                      <span className="text-[var(--text-tertiary)]">|</span>
                      <span className="text-[var(--text-secondary)] font-mono">{t('forecast.wizard.min_per_pkg', { n: Number(k.picking_time_per_paket) })}</span>
                    </div>
                    {hasExisting && (
                      <div className="mt-1.5 text-[9px] bg-amber-500/10 text-amber-500 px-1.5 py-0.5 rounded inline-block font-medium">
                        {t('forecast.wizard.existing_forecast_label', { status: '' })}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Existing forecast warning */}
          {existingForecast && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-card p-3 flex items-start gap-3">
              <div className="text-amber-400 flex-shrink-0 mt-0.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
              <div>
                <div className="text-sm font-semibold text-amber-400">{t('forecast.wizard.existing_forecast_label', { status: existingForecast.status })}</div>
                <div className="text-xs text-content-tertiary mt-0.5">
                  {t('forecast.wizard.existing_overwrite', { n: Number(existingForecast.pakete_gesamt).toLocaleString(locale) })}
                </div>
              </div>
            </div>
          )}

          {/* Selected kunde detail */}
          {selectedKunde && (
            <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: segColors[selectedKunde.segment] }} />
                  <span className="text-sm font-bold text-content">{selectedKunde.name}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-inset text-content-tertiary">{t('forecast.detail.segment', { seg: selectedKunde.segment })}</span>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-sm">
                <div><div className="text-[9px] text-[var(--text-tertiary)] uppercase">Pick-Time</div><div className="font-mono font-bold text-primary-400">{Number(selectedKunde.picking_time_per_paket)} min</div></div>
                <div><div className="text-[9px] text-[var(--text-tertiary)] uppercase">Pack-Time</div><div className="font-mono font-bold text-purple-400">{Number(selectedKunde.packing_time_per_paket)} min</div></div>
                <div><div className="text-[9px] text-[var(--text-tertiary)] uppercase">Cutoff</div><div className="font-mono font-bold text-content">{Number(selectedKunde.cutoff_stunde)}:00</div></div>
                <div><div className="text-[9px] text-[var(--text-tertiary)] uppercase">Vor Cutoff</div><div className="font-mono font-bold text-content">{Number(selectedKunde.vor_cutoff_pct)}%</div></div>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button onClick={() => { if (kundeId) setStep(2); }} disabled={!kundeId} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-forecast/20 text-forecast hover:bg-forecast/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">{t('common.next')}</button>
          </div>
        </div>
      )}

      {/* ═══ STEP 2: PAKETE ═══ */}
      {step === 2 && (
        <div className="space-y-5">
          <div className="flex items-center gap-2 text-sm text-content-tertiary">
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: segColors[selectedKunde?.segment] }} />
            <span className="text-content font-semibold">{selectedKunde?.name}</span>
            {rampupMap[Number(kundeId)] && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-cyan-500/12 text-cyan-400">{t('forecast.ai.rampup')}</span>
            )}
            <span className="text-[var(--text-tertiary)]">—</span>
            <span>{tMonth(wizMonth - 1)} {wizYear}</span>
            <span className="text-[var(--text-tertiary)]">—</span>
            <span className="font-mono">{arbeitstageInfo.arbeitstage} {t('forecast.wizard.working_days_label')}</span>
          </div>

          {/* Pakete Input */}
          <div>
            <span className="text-xs font-medium text-content-tertiary uppercase tracking-wider">{t('forecast.wizard.packages_total_label')}</span>
            <input
              type="number"
              value={pakete}
              onChange={(e) => setPakete(e.target.value)}
              min="0"
              step="100"
              placeholder={t('forecast.wizard.packages_placeholder')}
              className="mt-1.5 w-full bg-surface-inset border border-line-hover rounded-card px-4 py-4 text-2xl text-content font-mono focus:outline-none focus:border-forecast/40 focus:ring-1 focus:ring-forecast/20"
              autoFocus
            />
          </div>

          {/* Suggestion chips */}
          <div className="flex flex-wrap gap-2">
            {historicalSuggestion && (
              <button
                onClick={() => setPakete(String(historicalSuggestion))}
                className="inline-flex items-center gap-2 px-3.5 py-2 bg-forecast/10 border border-forecast/20 rounded-card text-sm hover:bg-forecast/20 transition-colors group"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2" className="flex-shrink-0"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/></svg>
                <div className="text-left">
                  <div className="font-mono font-bold text-forecast">{t('forecast.wizard.suggestion_label', { n: historicalSuggestion.toLocaleString(locale) })}</div>
                  <div className="text-[10px] text-[var(--text-secondary)]">{t('forecast.wizard.avg_last_months')}</div>
                </div>
                <span className="text-[10px] text-[var(--text-tertiary)] group-hover:text-forecast transition-colors ml-1">{t('forecast.wizard.apply_label')}</span>
              </button>
            )}
            {existingForecast && (
              <button
                onClick={() => setPakete(String(existingForecast.pakete_gesamt))}
                className="inline-flex items-center gap-2 px-3.5 py-2 bg-amber-500/10 border border-amber-500/20 rounded-card text-sm hover:bg-amber-500/20 transition-colors group"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" className="flex-shrink-0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <div className="text-left">
                  <div className="font-mono font-bold text-amber-400">{Number(existingForecast.pakete_gesamt).toLocaleString(locale)} {t('common.packages')}</div>
                  <div className="text-[10px] text-[var(--text-secondary)]">{t('forecast.wizard.existing_forecast_label', { status: existingForecast.status })}</div>
                </div>
                <span className="text-[10px] text-[var(--text-tertiary)] group-hover:text-amber-400 transition-colors ml-1">{t('forecast.wizard.apply_label')}</span>
              </button>
            )}
            {loadingAiSuggest && rampupMap[Number(kundeId)] && (
              <div className="inline-flex items-center gap-2 px-3.5 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-card text-sm">
                <svg className="animate-spin w-3.5 h-3.5 text-cyan-400" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="31.4 31.4" strokeLinecap="round"/></svg>
                <span className="text-cyan-400 text-xs">{t('forecast.ai.suggestion_computing')}</span>
              </div>
            )}
            {!loadingAiSuggest && aiRampupSuggestion && aiRampupSuggestion.suggested_volume > 0 && (
              <button
                onClick={() => setPakete(String(aiRampupSuggestion.suggested_volume))}
                className="inline-flex items-center gap-2 px-3.5 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-card text-sm hover:bg-cyan-500/20 transition-colors group"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" className="flex-shrink-0"><path d="M12 2a4 4 0 014 4c0 1.5-.8 2.8-2 3.5V11h3a3 3 0 013 3v1"/><circle cx="12" cy="6" r="1.5"/></svg>
                <div className="text-left">
                  <div className="font-mono font-bold text-cyan-400">AI: ~{aiRampupSuggestion.suggested_volume.toLocaleString(locale)} {t('common.packages')}</div>
                  <div className="text-[10px] text-[var(--text-secondary)]">{t('forecast.ai.growth_trend_confidence', { confidence: aiRampupSuggestion.confidence })}</div>
                </div>
                <span className="text-[10px] text-[var(--text-tertiary)] group-hover:text-cyan-400 transition-colors ml-1">{t('forecast.wizard.apply_label')}</span>
              </button>
            )}
          </div>
          {/* Ramp-up indicator */}
          {rampupMap[Number(kundeId)] && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/[0.06] border border-cyan-500/15">
              <span className="text-cyan-400 text-xs font-semibold">{t('forecast.ai.rampup')}</span>
              <span className="text-[10px] text-[var(--text-tertiary)]">
                {t('forecast.ai.rampup_info', { months: rampupMap[Number(kundeId)].months_active, orders: rampupMap[Number(kundeId)].total_orders.toLocaleString(locale) })}
              </span>
            </div>
          )}

          {/* Forecast v2: Auftragsprofil (Ø SKUs, Ø Artikel, SOP-Anteil) */}
          {kundeId && (
            <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-wider">{t('forecast.wizard.order_profile')}</div>
                {histSampleSize > 0 && (
                  <div className="text-[9px] text-[var(--text-tertiary)]">{t('forecast.ai.based_on_orders', { n: histSampleSize.toLocaleString(locale) })}</div>
                )}
              </div>
              {loadingProfil ? (
                <div className="text-xs text-[var(--text-tertiary)] animate-pulse">{t('forecast.ai.load_profile')}</div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[9px] text-[var(--text-tertiary)] uppercase block mb-1">{t('forecast.wizard.avg_skus_label')}</label>
                    <div className="flex items-center gap-1.5">
                      <input type="number" min="0.1" step="0.1" value={avgSkus} onChange={(e) => setAvgSkus(e.target.value)}
                        className="w-full bg-surface-inset border border-line-hover rounded-lg px-2.5 py-2 text-sm text-content font-mono focus:outline-none focus:border-forecast/40" />
                      {histAvgSkus != null && (
                        <span className={`text-[9px] flex-shrink-0 ${Math.abs(Number(avgSkus) - histAvgSkus) < 0.05 ? 'text-green-400' : 'text-amber-400'}`}
                          title={`${t('forecast.wizard.historical_label')}: ${histAvgSkus}`}>
                          {Math.abs(Number(avgSkus) - histAvgSkus) < 0.05 ? '✓' : '⚠'}
                        </span>
                      )}
                    </div>
                    {histAvgSkus != null && <div className="text-[8px] text-[var(--text-tertiary)] mt-0.5">{t('forecast.wizard.historical_label').toLowerCase()}: {histAvgSkus}</div>}
                  </div>
                  <div>
                    <label className="text-[9px] text-[var(--text-tertiary)] uppercase block mb-1">{t('forecast.wizard.avg_articles_label')}</label>
                    <div className="flex items-center gap-1.5">
                      <input type="number" min="0.1" step="0.1" value={avgArticles} onChange={(e) => setAvgArticles(e.target.value)}
                        className="w-full bg-surface-inset border border-line-hover rounded-lg px-2.5 py-2 text-sm text-content font-mono focus:outline-none focus:border-forecast/40" />
                      {histAvgArticles != null && (
                        <span className={`text-[9px] flex-shrink-0 ${Math.abs(Number(avgArticles) - histAvgArticles) < 0.05 ? 'text-green-400' : 'text-amber-400'}`}
                          title={`${t('forecast.wizard.historical_label')}: ${histAvgArticles}`}>
                          {Math.abs(Number(avgArticles) - histAvgArticles) < 0.05 ? '✓' : '⚠'}
                        </span>
                      )}
                    </div>
                    {histAvgArticles != null && <div className="text-[8px] text-[var(--text-tertiary)] mt-0.5">{t('forecast.wizard.historical_label').toLowerCase()}: {histAvgArticles}</div>}
                  </div>
                  <div>
                    <label className="text-[9px] text-[var(--text-tertiary)] uppercase block mb-1">{t('forecast.wizard.sop_share_label')}</label>
                    <div className="flex items-center gap-1.5">
                      <input type="number" min="0" max="100" step="1" value={sopAnteil} onChange={(e) => setSopAnteil(e.target.value)}
                        className="w-full bg-surface-inset border border-line-hover rounded-lg px-2.5 py-2 text-sm text-content font-mono focus:outline-none focus:border-forecast/40" />
                      {histSopAnteil != null && (
                        <span className={`text-[9px] flex-shrink-0 ${Math.abs(Number(sopAnteil) / 100 - histSopAnteil) < 0.01 ? 'text-green-400' : 'text-amber-400'}`}
                          title={`${t('forecast.wizard.historical_label')}: ${(histSopAnteil * 100).toFixed(1)}%`}>
                          {Math.abs(Number(sopAnteil) / 100 - histSopAnteil) < 0.01 ? '✓' : '⚠'}
                        </span>
                      )}
                    </div>
                    {histSopAnteil != null && <div className="text-[8px] text-[var(--text-tertiary)] mt-0.5">{t('forecast.wizard.historical_label').toLowerCase()}: {(histSopAnteil * 100).toFixed(1)}%</div>}
                  </div>
                </div>
              )}
              {/* Reset button */}
              {histAvgSkus != null && (
                <button
                  onClick={() => { setAvgSkus(String(histAvgSkus)); setAvgArticles(String(histAvgArticles)); setSopAnteil(String(Math.round(histSopAnteil * 100))); }}
                  className="mt-2 text-[10px] text-forecast hover:text-forecast/80 transition-colors"
                >{t('forecast.ai.reset_historical')}</button>
              )}
            </div>
          )}

          {/* Live Stats */}
          {pakete > 0 && preview && (
            <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
              <div className="text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('forecast.wizard.live_calc')}</div>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                <div className="bg-surface-card rounded-lg p-2.5 text-center shadow-card">
                  <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.working_days_label')}</div>
                  <div className="font-mono text-lg font-bold text-content">{preview.arbeitstage}</div>
                </div>
                <div className="bg-surface-card rounded-lg p-2.5 text-center shadow-card">
                  <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.avg_per_day_label')}</div>
                  <div className="font-mono text-lg font-bold text-forecast">{preview.avgPerDay.toLocaleString(locale)}</div>
                </div>
                <div className="bg-surface-card rounded-lg p-2.5 text-center shadow-card">
                  <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.pick_min_total')}</div>
                  <div className="font-mono text-lg font-bold text-primary-400">{fmt(preview.pickMinGesamt)}</div>
                </div>
                <div className="bg-surface-card rounded-lg p-2.5 text-center shadow-card">
                  <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.pack_min_total')}</div>
                  <div className="font-mono text-lg font-bold text-purple-400">{fmt(preview.packMinGesamt)}</div>
                </div>
              </div>
              {/* Time per order */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="bg-primary-500/5 border border-primary-500/10 rounded-lg p-2.5 text-center">
                  <div className="text-[9px] text-primary-400/60 uppercase">{t('forecast.wizard.pick_per_order')}</div>
                  <div className="font-mono text-lg font-extrabold text-content">{preview.pickProAuftrag.toFixed(1)}min</div>
                  <div className="text-[9px] text-[var(--text-tertiary)]">{fmt(preview.pickHGesamt)}h {t('forecast.wizard.total_label')}</div>
                </div>
                <div className="bg-purple-500/5 border border-purple-500/10 rounded-lg p-2.5 text-center">
                  <div className="text-[9px] text-purple-400/60 uppercase">{t('forecast.wizard.pack_per_order')}</div>
                  <div className="font-mono text-lg font-extrabold text-content">{preview.packProAuftrag.toFixed(1)}min</div>
                  <div className="text-[9px] text-[var(--text-tertiary)]">{fmt(preview.packHGesamt)}h {t('forecast.wizard.total_label')}</div>
                </div>
              </div>
              {/* FTE row */}
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div className="bg-primary-500/5 border border-primary-500/10 rounded-lg p-2.5 text-center">
                  <div className="text-[9px] text-primary-400/60 uppercase">{t('forecast.grid.pick_fte')}</div>
                  <div className="font-mono text-xl font-extrabold text-content">{preview.pickFte.toFixed(2)}</div>
                  <div className="text-[9px] text-[var(--text-tertiary)]">{fmt(preview.pickHGesamt)}h</div>
                </div>
                <div className="bg-purple-500/5 border border-purple-500/10 rounded-lg p-2.5 text-center">
                  <div className="text-[9px] text-purple-400/60 uppercase">{t('forecast.grid.pack_fte')}</div>
                  <div className="font-mono text-xl font-extrabold text-content">{preview.packFte.toFixed(2)}</div>
                  <div className="text-[9px] text-[var(--text-tertiary)]">{fmt(preview.packHGesamt)}h</div>
                </div>
                <div className="bg-surface-card border border-line-hover rounded-lg p-2.5 text-center">
                  <div className="text-[9px] text-content-tertiary uppercase">{t('forecast.wizard.total_fte')}</div>
                  <div className="font-mono text-xl font-extrabold text-content">{(preview.pickFte + preview.packFte).toFixed(2)}</div>
                  <div className="text-[9px] text-[var(--text-tertiary)]">{fmt(preview.pickHGesamt + preview.packHGesamt)}h</div>
                </div>
              </div>
              {/* SOP savings hint */}
              {preview.sopAnteilDecimal > 0 && preview.pickOhneSop > 0 && (
                <div className="mt-2 text-[9px] text-cyan-400/70 bg-cyan-400/5 border border-cyan-400/10 rounded-lg px-3 py-1.5">
                  {t('forecast.wizard.sop_savings', { pick: Math.round((1 - preview.pickHGesamt / preview.pickOhneSop) * 100), pack: Math.round((1 - preview.packHGesamt / preview.packOhneSop) * 100) })}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(1)} className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">{t('common.back')}</button>
            <button onClick={() => { if (pakete > 0) setStep(3); }} disabled={!pakete || Number(pakete) <= 0} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-forecast/20 text-forecast hover:bg-forecast/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">{t('common.next')}</button>
          </div>
        </div>
      )}

      {/* ═══ STEP 3: ZEITVORGABEN PRÜFEN (NEU) ═══ */}
      {step === 3 && preview && (
        <div className="space-y-5">
          <div className="text-sm text-content-tertiary">
            {t('forecast.wizard.time_targets_desc', { name: selectedKunde?.name, packages: Number(pakete).toLocaleString(locale), month: tMonth(wizMonth - 1), year: wizYear })}
          </div>

          {/* Summary cards */}
          <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
            <div className="px-4 py-2.5 border-b border-line">
              <span className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('forecast.wizard.time_targets_weighted')}</span>
            </div>
            <div className="grid grid-cols-2 divide-x divide-line">
              <div className="p-4 text-center">
                <div className="text-[9px] text-primary-400/60 uppercase mb-1">{t('forecast.wizard.pick_per_order')}</div>
                <div className="font-mono text-2xl font-extrabold text-content">{preview.pickProAuftrag.toFixed(1)}min</div>
                {preview.sopAnteilDecimal > 0 && (
                  <div className="mt-1 text-[9px] text-[var(--text-tertiary)]">
                    SOP ({(preview.sopAnteilDecimal * 100).toFixed(0)}%): {preview.sopPick.toFixed(1).replace('.', ',')}min | Normal: {preview.normalPick.toFixed(1).replace('.', ',')}min
                  </div>
                )}
              </div>
              <div className="p-4 text-center">
                <div className="text-[9px] text-purple-400/60 uppercase mb-1">{t('forecast.wizard.pack_per_order')}</div>
                <div className="font-mono text-2xl font-extrabold text-content">{preview.packProAuftrag.toFixed(1)}min</div>
                {preview.sopAnteilDecimal > 0 && (
                  <div className="mt-1 text-[9px] text-[var(--text-tertiary)]">
                    SOP ({(preview.sopAnteilDecimal * 100).toFixed(0)}%): {preview.sopPack.toFixed(1).replace('.', ',')}min | Normal: {preview.normalPack.toFixed(1).replace('.', ',')}min
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Capacity table */}
          <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
            <div className="px-4 py-2.5 border-b border-line">
              <span className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('forecast.wizard.capacity_demand')}</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                  <th className="text-left px-4 py-2 font-medium"></th>
                  <th className="text-right px-3 py-2 font-medium">{t('forecast.wizard.hours_label')}</th>
                  <th className="text-right px-3 py-2 font-medium">FTE</th>
                  {preview.sopAnteilDecimal > 0 && <th className="text-right px-3 py-2 font-medium text-[var(--text-tertiary)]">{t('forecast.wizard.without_sop')}</th>}
                  {preview.sopAnteilDecimal > 0 && <th className="text-right px-3 py-2 font-medium text-[var(--text-tertiary)]">{t('forecast.wizard.savings')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                <tr>
                  <td className="px-4 py-2.5 text-primary-400 font-medium">{t('forecast.wizard.pick_label')}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold text-primary-400">{Math.round(preview.pickHGesamt)}h</td>
                  <td className="px-3 py-2.5 text-right font-mono text-primary-400">{preview.pickFte.toFixed(1)}</td>
                  {preview.sopAnteilDecimal > 0 && <td className="px-3 py-2.5 text-right font-mono text-[var(--text-tertiary)]">{Math.round(preview.pickOhneSop)}h</td>}
                  {preview.sopAnteilDecimal > 0 && <td className="px-3 py-2.5 text-right font-mono text-green-400">-{Math.round((1 - preview.pickHGesamt / preview.pickOhneSop) * 100)}%</td>}
                </tr>
                <tr>
                  <td className="px-4 py-2.5 text-purple-400 font-medium">{t('forecast.wizard.pack_label')}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold text-purple-400">{Math.round(preview.packHGesamt)}h</td>
                  <td className="px-3 py-2.5 text-right font-mono text-purple-400">{preview.packFte.toFixed(1)}</td>
                  {preview.sopAnteilDecimal > 0 && <td className="px-3 py-2.5 text-right font-mono text-[var(--text-tertiary)]">{Math.round(preview.packOhneSop)}h</td>}
                  {preview.sopAnteilDecimal > 0 && <td className="px-3 py-2.5 text-right font-mono text-green-400">-{Math.round((1 - preview.packHGesamt / preview.packOhneSop) * 100)}%</td>}
                </tr>
              </tbody>
              <tfoot className="border-t border-line-hover bg-surface-card">
                <tr className="font-bold">
                  <td className="px-4 py-2.5 text-content">{t('common.total')}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-content">{Math.round(preview.pickHGesamt + preview.packHGesamt)}h</td>
                  <td className="px-3 py-2.5 text-right font-mono text-content">{(preview.pickFte + preview.packFte).toFixed(1)}</td>
                  {preview.sopAnteilDecimal > 0 && <td className="px-3 py-2.5 text-right font-mono text-[var(--text-tertiary)]">{Math.round(preview.pickOhneSop + preview.packOhneSop)}h</td>}
                  {preview.sopAnteilDecimal > 0 && <td className="px-3 py-2.5 text-right font-mono text-green-400">-{Math.round((1 - (preview.pickHGesamt + preview.packHGesamt) / (preview.pickOhneSop + preview.packOhneSop)) * 100)}%</td>}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Basis info */}
          <div className="text-[9px] text-[var(--text-tertiary)] space-y-0.5">
            <div>{t('forecast.wizard.basis_info', { skus: avgSkus, articles: avgArticles, sop: sopAnteil })}</div>
            <div>{t('forecast.wizard.pick_formula', { formula: selectedKunde?.pick_alpha != null ? t('forecast.wizard.ai_formula') : t('forecast.wizard.flat_rate', { n: (Number(selectedKunde?.picking_time_per_paket) || 2).toFixed(1) }) })} | {t('forecast.wizard.pack_formula', { formula: selectedKunde?.pack_alpha != null ? t('forecast.wizard.ai_formula') : t('forecast.wizard.flat_rate', { n: (Number(selectedKunde?.packing_time_per_paket) || 3).toFixed(1) }) })}</div>
            {histSampleSize > 0 && <div>{t('forecast.wizard.historical_basis', { n: histSampleSize.toLocaleString(locale) })}</div>}
          </div>

          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(2)} className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">{t('common.back')}</button>
            <button onClick={() => setStep(4)} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-forecast/20 text-forecast hover:bg-forecast/30 transition-colors">{t('common.next')}</button>
          </div>
        </div>
      )}

      {/* ═══ STEP 4: GEWICHTUNG ═══ */}
      {step === 4 && (
        <div className="space-y-5">
          <div className="text-sm text-content-tertiary">
            {t('forecast.wizard.import_weighting_desc', { time: preview ? `${preview.cutoffStunde}:00` : '14:00' })}
          </div>

          {/* Presets */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(presets).map(([key, p]) => (
              <button key={key} onClick={() => setWeights((w) => ({ ...w, ...p.values }))} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-inset border border-line-hover text-content-tertiary hover:text-content hover:bg-surface-active transition-colors">{t(p.labelKey)}</button>
            ))}
          </div>

          {/* Sliders — Mo-Fr */}
          <div className="space-y-3">
            {['montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag'].map((key) => {
              const pct = weights[key] / 2.0;
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="w-8 text-xs font-bold text-content-tertiary">{t(weekdayLabelKeys[key])}</span>
                  <div className="flex-1 relative">
                    <input type="range" min="0.1" max="2.0" step="0.1" value={weights[key]} onChange={(e) => setWeights((w) => ({ ...w, [key]: Number(e.target.value) }))} className="w-full h-1.5 bg-surface-active rounded-full appearance-none cursor-pointer accent-forecast" />
                  </div>
                  <span className="w-10 text-right font-mono text-sm font-bold text-content">{weights[key].toFixed(1)}</span>
                  <div className="w-16 h-2 bg-surface-inset rounded-full overflow-hidden">
                    <div className="h-full bg-forecast/50 rounded-full transition-all" style={{ width: `${pct * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Separator + Wochenende */}
          <div className="border-t border-line-hover pt-3">
            <div className="text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-3">{t('forecast.wizard.weekend_import_volume')}</div>
            <div className="space-y-3">
              {['samstag', 'sonntag'].map((key) => {
                const pct = weights[key] / 2.0;
                return (
                  <div key={key} className="flex items-center gap-3">
                    <span className="w-8 text-xs font-bold text-content-tertiary">{t(weekdayLabelKeys[key])}</span>
                    <div className="flex-1 relative">
                      <input type="range" min="0.0" max="1.5" step="0.1" value={weights[key]} onChange={(e) => setWeights((w) => ({ ...w, [key]: Number(e.target.value) }))} className="w-full h-1.5 bg-surface-active rounded-full appearance-none cursor-pointer accent-orange-400" />
                    </div>
                    <span className="w-10 text-right font-mono text-sm font-bold text-content">{weights[key].toFixed(1)}</span>
                    <div className="w-16 h-2 bg-surface-inset rounded-full overflow-hidden">
                      <div className="h-full bg-orange-400/50 rounded-full transition-all" style={{ width: `${pct / 1.5 * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-[var(--text-tertiary)] mt-2">{t('forecast.wizard.weekend_accumulation_hint')}</div>
          </div>

          {/* Preview bar chart — zeigt VERSANDLAST pro Arbeitstag */}
          {preview && (
            <div className="bg-surface-card border border-line rounded-card p-4 shadow-card">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-medium text-[var(--text-secondary)] uppercase tracking-wider">{t('forecast.wizard.shipping_load_preview')}</div>
                <div className="text-[10px] text-[var(--text-tertiary)] font-mono">{t('forecast.wizard.max_per_day', { n: preview.maxPkt.toLocaleString(locale) })}</div>
              </div>
              <div className="flex items-end gap-[2px]" style={{ height: 90 }}>
                {preview.dailyPkts.map((pkt, i) => {
                  const h = pkt > 0 ? Math.max((pkt / preview.maxPkt) * 100, 4) : 0;
                  const d = new Date(wizYear, wizMonth - 1, i + 1);
                  const isWe = d.getDay() === 0 || d.getDay() === 6;
                  const isFt = feiertagSet.has(isoDate(d));
                  const isMo = d.getDay() === 1;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end" style={{ height: '100%' }}>
                      <div
                        className={`w-full rounded-t transition-all duration-200 ${isFt ? 'bg-red-500/30' : isWe ? 'bg-surface-inset' : isMo ? 'bg-forecast/60' : 'bg-forecast/40'}`}
                        style={{ height: `${h}%` }}
                        title={`${i + 1}. ${isFt ? `(${t('forecast.wizard.legend_holiday')})` : isWe ? `(${t('forecast.wizard.legend_weekend')})` : isMo ? `(${t('forecast.wizard.legend_monday')})` : ''}: ${pkt}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-[2px] mt-1">
                {preview.dailyPkts.map((_, i) => (
                  <div key={i} className="flex-1 text-center text-[7px] text-[var(--text-tertiary)] font-mono">{(i + 1) % 5 === 0 || i === 0 ? i + 1 : ''}</div>
                ))}
              </div>
              {/* Legend */}
              <div className="flex gap-3 mt-3">
                <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded bg-forecast/60" /><span className="text-[9px] text-[var(--text-secondary)]">{t('forecast.wizard.legend_monday')}</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded bg-forecast/40" /><span className="text-[9px] text-[var(--text-secondary)]">{t('forecast.wizard.legend_workday')}</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded bg-red-500/30" /><span className="text-[9px] text-[var(--text-secondary)]">{t('forecast.wizard.legend_holiday')}</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded bg-surface-inset" /><span className="text-[9px] text-[var(--text-secondary)]">{t('forecast.wizard.legend_weekend')}</span></div>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(3)} className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">{t('common.back')}</button>
            <button onClick={() => setStep(5)} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-forecast/20 text-forecast hover:bg-forecast/30 transition-colors">{t('common.next')}</button>
          </div>
        </div>
      )}

      {/* ═══ STEP 5: BESTÄTIGUNG ═══ */}
      {step === 5 && (
        <div className="space-y-5">
          <div className="text-sm text-content-tertiary">{t('forecast.wizard.confirmation_desc')}</div>

          <div className="bg-surface-card border border-line rounded-card divide-y divide-line shadow-card">
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-[var(--text-secondary)]">{t('forecast.wizard.customer_label')}</span>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: segColors[selectedKunde?.segment] }} />
                <span className="text-sm font-semibold text-content">{selectedKunde?.name}</span>
                <span className="text-[10px] text-[var(--text-secondary)]">({selectedKunde?.segment})</span>
              </div>
            </div>
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-[var(--text-secondary)]">{t('forecast.wizard.period')}</span>
              <span className="text-sm font-mono text-content">{tMonth(wizMonth - 1)} {wizYear}</span>
            </div>
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-[var(--text-secondary)]">{t('forecast.wizard.packages_total_label')}</span>
              <span className="text-sm font-mono font-bold text-forecast">{Number(pakete).toLocaleString(locale)}</span>
            </div>
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-[var(--text-secondary)]">{t('forecast.wizard.working_days_label')}</span>
              <span className="text-sm font-mono text-content">{t('forecast.wizard.working_days_count', { n: preview?.arbeitstage, holidays: arbeitstageInfo.feiertageImMonat })}</span>
            </div>
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-[var(--text-secondary)]">{t('forecast.wizard.avg_packages_day')}</span>
              <span className="text-sm font-mono text-content">{preview?.avgPerDay?.toLocaleString(locale)}</span>
            </div>
            {Number(sopAnteil) > 0 && (
              <div className="flex justify-between px-4 py-3">
                <span className="text-sm text-[var(--text-secondary)]">{t('forecast.wizard.order_profile')}</span>
                <span className="text-[11px] font-mono text-content-secondary">
                  {t('forecast.wizard.order_profile_summary', { skus: avgSkus, articles: avgArticles, sop: sopAnteil, pick: preview?.pickProAuftrag?.toFixed(1), pack: preview?.packProAuftrag?.toFixed(1) })}
                </span>
              </div>
            )}
            <div className="grid grid-cols-3 divide-x divide-line">
              <div className="px-4 py-3 text-center">
                <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.grid.pick_fte')}</div>
                <div className="font-mono text-base font-bold text-primary-400">{preview?.pickFte?.toFixed(2)}</div>
              </div>
              <div className="px-4 py-3 text-center">
                <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.grid.pack_fte')}</div>
                <div className="font-mono text-base font-bold text-purple-400">{preview?.packFte?.toFixed(2)}</div>
              </div>
              <div className="px-4 py-3 text-center">
                <div className="text-[9px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.total_fte')}</div>
                <div className="font-mono text-base font-bold text-content">{preview ? (preview.pickFte + preview.packFte).toFixed(2) : '-'}</div>
              </div>
            </div>
            <div className="px-4 py-3">
              <span className="text-sm text-[var(--text-secondary)] block mb-2">{t('forecast.wizard.weighting_label')}</span>
              <div className="flex gap-3">
                {weekdayKeys.map((k) => (
                  <div key={k} className="text-center flex-1">
                    <div className="text-[10px] text-[var(--text-tertiary)]">{t(weekdayLabelKeys[k])}</div>
                    <div className="font-mono text-sm font-bold text-content">{weights[k].toFixed(1)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {existingForecast && (
            <div className="bg-amber-500/5 border border-amber-500/15 rounded-card px-4 py-2.5 text-xs text-amber-400">
              {t('forecast.wizard.existing_overwrite', { n: Number(existingForecast.pakete_gesamt).toLocaleString(locale) })}
            </div>
          )}

          {/* Tages-Verteilungstabelle */}
          {preview && (
            <div className="bg-surface-card border border-line rounded-card overflow-hidden shadow-card">
              <div className="px-4 py-2.5 border-b border-line flex items-center justify-between">
                <span className="text-[10px] font-bold text-content-tertiary uppercase tracking-wider">{t('forecast.wizard.daily_distribution')}</span>
                <span className="text-[10px] text-[var(--text-tertiary)] font-mono">{preview.arbeitstage} {t('forecast.wizard.working_days_label')}</span>
              </div>
              <div className="max-h-[240px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-surface-elevated/95 backdrop-blur-sm">
                    <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">
                      <th className="text-left px-4 py-2 font-medium">{t('forecast.wizard.date_col')}</th>
                      <th className="text-left px-2 py-2 font-medium">{t('forecast.wizard.day_col')}</th>
                      <th className="text-right px-2 py-2 font-medium">{t('forecast.wizard.weight_col')}</th>
                      <th className="text-right px-4 py-2 font-medium">{t('forecast.wizard.soll_packages')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {(() => {
                      const dim = new Date(wizYear, wizMonth, 0).getDate();
                      const jsDayToKey = ['sonntag', 'montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag', 'samstag'];
                      const dayNames = Array.from({ length: 7 }, (_, i) => t(`common.weekdays.${i}`));
                      const rows = [];
                      for (let d = 1; d <= dim; d++) {
                        const date = new Date(wizYear, wizMonth - 1, d);
                        const jsDay = date.getDay();
                        const isWeekend = jsDay === 0 || jsDay === 6;
                        const ds = isoDate(date);
                        const isFt = feiertagSet.has(ds);
                        if (isWeekend || isFt) continue;
                        const dayKey = jsDayToKey[jsDay];
                        const pkt = preview.dailyPkts[d - 1] || 0;
                        const isSpitze = d > 1 && (() => {
                          const prev = new Date(wizYear, wizMonth - 1, d - 1);
                          return prev.getDay() === 0 || prev.getDay() === 6 || feiertagSet.has(isoDate(prev));
                        })();
                        rows.push(
                          <tr key={d} className="hover:bg-surface-card transition-colors">
                            <td className="px-4 py-1.5 font-mono text-xs text-content-secondary">
                              {d}.{wizMonth}.{wizYear}
                            </td>
                            <td className="px-2 py-1.5 text-xs">
                              <span className="text-content-tertiary">{dayNames[jsDay]}</span>
                              {isSpitze && <span className="ml-1.5 text-[9px] bg-amber-500/10 text-amber-500 px-1 py-0.5 rounded">{t('forecast.wizard.peak_label')}</span>}
                            </td>
                            <td className="px-2 py-1.5 text-right font-mono text-xs text-[var(--text-secondary)]">{(weights[dayKey] || 1).toFixed(1)}</td>
                            <td className="px-4 py-1.5 text-right font-mono text-xs font-bold text-content">{pkt.toLocaleString(locale)}</td>
                          </tr>
                        );
                      }
                      return rows;
                    })()}
                  </tbody>
                  <tfoot className="border-t border-line-hover bg-surface-card">
                    <tr>
                      <td className="px-4 py-2 text-xs font-bold text-content-tertiary" colSpan={3}>{t('common.total')}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs font-extrabold text-forecast">
                        {preview.dailyPkts.reduce((s, p) => s + p, 0).toLocaleString(locale)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {wizError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-card px-4 py-3 text-sm text-red-400">
              {wizError}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(4)} className="px-4 py-2 rounded-lg text-sm text-content-tertiary hover:text-content hover:bg-surface-hover transition-colors">{t('common.back')}</button>
            <div className="flex gap-2">
              <button onClick={doSaveEntwurf} disabled={saving || loading} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-surface-inset border border-line-hover text-content-secondary hover:bg-surface-active hover:text-content disabled:opacity-50 transition-colors flex items-center gap-2">
                {saving ? (
                  <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="60 30" /></svg> {t('forecast.wizard.saving_label')}</>
                ) : (
                  <>{t('forecast.wizard.save_as_draft')}</>
                )}
              </button>
              <button onClick={doVerteilen} disabled={loading || saving} className="px-6 py-2.5 rounded-lg text-sm font-bold bg-forecast text-white hover:bg-forecast/90 disabled:opacity-50 transition-colors flex items-center gap-2">
                {loading ? (
                  <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="60 30" /></svg> {t('forecast.wizard.distributing_label')}</>
                ) : (
                  <>{t('forecast.wizard.confirm_distribute')}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ STEP 6: RESULT ═══ */}
      {step === 6 && result && (
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
          </div>
          <div className="text-lg font-bold text-content">{t('forecast.wizard.success_distributed')}</div>
          <div className="text-sm text-content-tertiary">
            {t('forecast.wizard.packages_distributed', { packages: (Number(result.monats_forecast?.pakete_gesamt) || 0).toLocaleString(locale), days: result.monats_forecast?.arbeitstage || '–' })}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4 max-w-sm mx-auto">
            <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
              <div className="text-[10px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.pick_fte_avg')}</div>
              <div className="font-mono text-xl font-bold text-primary-400">{(Number(result.monats_forecast?.benoetigte_pick_fte) || 0).toFixed(2)}</div>
            </div>
            <div className="bg-surface-card border border-line rounded-card p-3 shadow-card">
              <div className="text-[10px] text-[var(--text-tertiary)] uppercase">{t('forecast.wizard.pack_fte_avg')}</div>
              <div className="font-mono text-xl font-bold text-purple-400">{(Number(result.monats_forecast?.benoetigte_pack_fte) || 0).toFixed(2)}</div>
            </div>
          </div>

          {/* Mini daily chart */}
          {result.tages_forecasts && (
            <div className="bg-surface-card border border-line rounded-card p-4 max-w-md mx-auto shadow-card">
              <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-2">{t('forecast.wizard.daily_distribution')}</div>
              <div className="flex items-end gap-[2px]" style={{ height: 60 }}>
                {result.tages_forecasts.map((tf, i) => {
                  const soll = Number(tf.soll_pakete) || 0;
                  const maxSoll = Math.max(...result.tages_forecasts.map(t => Number(t.soll_pakete) || 0), 1);
                  const h = soll > 0 ? Math.max((soll / maxSoll) * 100, 3) : 0;
                  return (
                    <div key={i} className="flex-1 flex items-end justify-center" style={{ height: '100%' }}>
                      <div className={`w-full rounded-t ${tf.ist_spitzentag ? 'bg-amber-500/40' : tf.ist_arbeitstag ? 'bg-forecast/40' : 'bg-surface-inset'}`} style={{ height: `${h}%` }} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <button onClick={() => { onClose(); onComplete(); }} className="mt-4 px-6 py-2.5 rounded-lg text-sm font-semibold bg-surface-active text-content hover:bg-surface-active transition-colors">
            {t('forecast.wizard.go_to_grid')}
          </button>
        </div>
      )}
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SEGMENT SUMMEN BAR — A/B/C + GESAMT SOLL/IST overview
// ═══════════════════════════════════════════════════════════════════

function SegmentSummenBar({ segSummen, t, locale }) {
  if (!segSummen || !segSummen.segmente || segSummen.segmente.length === 0) return null;
  const allColors = { A: '#338edc', B: '#4D9CE0', C: '#F59E0B', GESAMT: '#22D3EE' };
  const items = [...segSummen.segmente, segSummen.gesamt];

  return (
    <div className="grid grid-cols-4 gap-[14px] mb-5">
      {items.map((s) => {
        const color = allColors[s.segment] || '#6b7280';
        const hasIst = s.ist > 0;
        return (
          <div key={s.segment} className="bg-surface-card rounded-xl shadow-card flex items-center gap-3"
            style={{ borderLeft: `3px solid ${color}`, padding: '12px 16px' }}>
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold uppercase tracking-wide" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{s.segment}</span>
                <span className="text-[11px] text-content-tertiary">{t('forecast.segment_bar.customers_count', { n: s.kunden })}</span>
              </div>
              <div className="flex items-baseline gap-2.5 mt-0.5">
                <span className="font-bold text-content" style={{ fontSize: 18 }}>{s.soll.toLocaleString(locale)}</span>
                {hasIst && (
                  <span className="font-semibold text-content" style={{ fontSize: 14 }}>{s.ist.toLocaleString(locale)}</span>
                )}
                {!hasIst && (
                  <span className="text-[var(--text-secondary)]" style={{ fontSize: 14 }}>–</span>
                )}
                {hasIst && (
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{s.erfuellung}%</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// KPI BAR (was SummaryCards) — with HEUTE LIVE badge
// ═══════════════════════════════════════════════════════════════════

function KpiBar({ monatsData, gridData, liveData, intradayData, t, locale }) {
  const totalSoll = monatsData.reduce((s, m) => s + Number(m.pakete_gesamt), 0);
  const totalIst = gridData.reduce((s, r) => s + (r.ist_pakete != null ? Number(r.ist_pakete) : 0), 0);
  const erfuellung = totalSoll > 0 ? Math.round((totalIst / totalSoll) * 100) : 0;
  const avgPickFte = monatsData.length > 0 ? monatsData.reduce((s, m) => s + (Number(m.benoetigte_pick_fte) || 0), 0) / monatsData.length : 0;
  const avgPackFte = monatsData.length > 0 ? monatsData.reduce((s, m) => s + (Number(m.benoetigte_pack_fte) || 0), 0) / monatsData.length : 0;
  const totalFte = avgPickFte + avgPackFte;

  // Intraday totals from forecast_snapshots (preferred over fulfillment_snapshots)
  const intraArr = Array.isArray(intradayData) ? intradayData : [];
  const intraDone = intraArr.reduce((s, d) => s + (d.pickings_done || 0), 0);
  const intraTodo = intraArr.reduce((s, d) => s + (d.pickings_todo || 0), 0);
  const intraSoll = intraArr.reduce((s, d) => s + (d.soll_pakete || 0), 0);
  const hasIntraday = intraArr.length > 0;

  // Use intraday if available, fallback to liveData
  const liveDone = hasIntraday ? intraDone : (liveData?.gesamt?.erledigt || 0);
  const liveOffen = hasIntraday ? intraTodo : (liveData?.gesamt?.offen || 0);
  const liveSoll = hasIntraday ? intraSoll : (liveData?.gesamt?.soll || 0);
  const livePct = liveSoll > 0 ? Math.round((liveDone / liveSoll) * 1000) / 10 : 0;
  const hasLive = hasIntraday || !!liveData;

  return (
    <div className="grid grid-cols-2 gap-[14px] mb-5" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr 1.2fr' }}>
      <div className="bg-surface-card rounded-card shadow-card px-[22px] py-[18px]">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-content-secondary mb-1.5">{t('forecast.kpi.packages_soll')}</div>
        <div className="text-[26px] font-bold text-content">{totalSoll.toLocaleString(locale)}</div>
      </div>
      <div className="bg-surface-card rounded-card shadow-card px-[22px] py-[18px]">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-content-secondary mb-1.5">{t('forecast.kpi.packages_ist')}</div>
        <div className="text-[26px] font-bold text-content">{totalIst > 0 ? totalIst.toLocaleString(locale) : '–'}</div>
      </div>
      <div className="bg-surface-card rounded-card shadow-card px-[22px] py-[18px]">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-content-secondary mb-1.5">{t('forecast.kpi.fulfillment')}</div>
        <div className="text-[26px] font-bold text-content">{totalIst > 0 ? `${erfuellung}%` : '–'}</div>
      </div>
      <div className="bg-surface-card rounded-card shadow-card px-[22px] py-[18px]">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-content-secondary mb-1.5">{t('forecast.kpi.total_fte')}</div>
        <div className="text-[26px] font-bold text-content">{totalFte.toFixed(1)}</div>
        <div className="text-[11px] text-content-tertiary mt-0.5">{t('forecast.kpi.pick_pack_short', { pick: avgPickFte.toFixed(1), pack: avgPackFte.toFixed(1) })}</div>
      </div>
      {hasLive ? (
        <div className="rounded-card px-[22px] py-[18px] relative overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #4D9CE0 100%)' }}>
          <div className="absolute w-[100px] h-[100px] rounded-full" style={{ background: 'rgba(255,255,255,0.08)', top: -30, right: -10 }} />
          <div className="relative">
            <div className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>● {t('forecast.kpi.today_live')}</div>
            <div className="text-[30px] font-bold">{liveDone.toLocaleString(locale)}</div>
            <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('forecast.kpi.soll_label', { n: liveSoll.toLocaleString(locale) })}</div>
            <div className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('forecast.kpi.open_prognose', { open: liveOffen.toLocaleString(locale), pct: livePct })}</div>
          </div>
        </div>
      ) : (
        <div className="rounded-card px-[22px] py-[18px] relative overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #4D9CE0 100%)' }}>
          <div className="absolute w-[100px] h-[100px] rounded-full" style={{ background: 'rgba(255,255,255,0.08)', top: -30, right: -10 }} />
          <div className="relative">
            <div className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>● {t('forecast.kpi.today_live')}</div>
            <div className="text-[30px] font-bold">–</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════

export default function Forecast() {
  const { t, locale, tMonth, tMonthShort } = useLanguage();
  const now = new Date();
  // #1635: Deep-Link-Vorbelegung aus dem Cockpit (?wizard=1&kunde_id=&jahr=&monat=)
  const [initialParams] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    const jahr = parseInt(sp.get('jahr'), 10);
    const monat = parseInt(sp.get('monat'), 10);
    return {
      wizard: sp.get('wizard') === '1' || !!sp.get('kunde_id'),
      kundeId: sp.get('kunde_id') || '',
      jahr: jahr >= 2020 && jahr <= 2100 ? jahr : null,
      monat: monat >= 1 && monat <= 12 ? monat : null,
    };
  });
  const [year, setYear] = useState(initialParams.jahr || now.getFullYear());
  const [month, setMonth] = useState(initialParams.monat || now.getMonth() + 1);
  const [gridData, setGridData] = useState([]);
  const [monatsData, setMonatsData] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(initialParams.wizard);
  const [viewMode, setViewMode] = useState('tag');
  const [standorte, setStandorte] = useState([]);
  const [standortId, setStandortId] = useState('alle');
  const [showWeekends, setShowWeekends] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [intradayData, setIntradayData] = useState(null);
  const [weekendActivity, setWeekendActivity] = useState([]);
  const [segmentWarning, setSegmentWarning] = useState(0);
  const [segSummen, setSegSummen] = useState(null);
  const [rampupMap, setRampupMap] = useState({});
  const [fteBedarf, setFteBedarf] = useState(null);
  const [demandMap, setDemandMap] = useState({});
  const [aktionen, setAktionen] = useState([]);
  const [feiertageGrid, setFeiertageGrid] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  useEffect(() => {
    api('/api/standorte').then((s) => { if (Array.isArray(s)) setStandorte(s); });
    api('/api/segment-vorschlaege/count').then(d => setSegmentWarning(d.count || 0)).catch(() => {});
  }, []);

  const loadData = useCallback(() => {
    const sp = standortId !== 'alle' ? `&standort_id=${standortId}` : '';
    api(`/api/forecast/grid?jahr=${year}&monat=${month}${sp}`).then(setGridData).catch(() => setGridData([]));
    api(`/api/forecast/monats?jahr=${year}&monat=${month}${sp}`).then(setMonatsData).catch(() => setMonatsData([]));
    api(`/api/forecast/weekend-activity?jahr=${year}&monat=${month}`).then(d => setWeekendActivity(d.dates || [])).catch(() => setWeekendActivity([]));
    api(`/api/forecast/segment-summen?jahr=${year}&monat=${month}${sp}`).then(setSegSummen).catch(() => setSegSummen(null));
    api('/api/forecast/rampup-customers').then(d => {
      const map = {};
      (d.customers || []).forEach(c => { map[c.kunde_id] = c; });
      setRampupMap(map);
    }).catch(() => setRampupMap({}));
    api(`/api/forecast/fte-berechnung?jahr=${year}&monat=${month}${sp}`).then(setFteBedarf).catch(() => setFteBedarf(null));
    // Load Aktionszeiträume for this month
    const daysInM = new Date(year, month, 0).getDate();
    const from = `${year}-${String(month).padStart(2, '0')}-01`;
    const to = `${year}-${String(month).padStart(2, '0')}-${daysInM}`;
    api(`/api/aktionszeitraeume?von=${from}&bis=${to}`).then(setAktionen).catch(() => setAktionen([]));
    // Load Feiertage for grid holiday marking
    api(`/api/feiertage?jahr=${year}`).then(setFeiertageGrid).catch(() => setFeiertageGrid([]));
    // Load demand data for grid coloring + summary rows
    api(`/api/forecast/daily-demand/range?from=${from}&to=${to}`).then(d => {
      const map = {};
      (d.entries || []).forEach(e => {
        // Parse date in Vienna timezone (pg DATE serialized as UTC midnight-1h)
        let dateStr = '';
        if (e.date) {
          const dt = new Date(e.date);
          dateStr = dt.toLocaleDateString('sv-SE', { timeZone: 'Europe/Vienna' });
        }
        if (!dateStr) return;
        if (!map[dateStr]) map[dateStr] = {};
        const key = (e.customer_name || '').toLowerCase().trim();
        if (key) map[dateStr][key] = { demand: e.total_demand || 0, done: e.pickings_done || 0, todo: e.picking_todo || 0 };
      });
      setDemandMap(map);
    }).catch(() => setDemandMap({}));
  }, [year, month, standortId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Fetch live data + intraday for current month only
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  useEffect(() => {
    if (!isCurrentMonth) { setLiveData(null); setIntradayData(null); return; }
    let mounted = true;
    const fetchLive = () => {
      api('/api/forecast/heute-live').then(d => { if (mounted) setLiveData(d); }).catch(() => {});
      api('/api/forecast/intraday').then(d => { if (mounted) setIntradayData(d); }).catch(() => {});
    };
    fetchLive();
    const iv = setInterval(fetchLive, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, [isCurrentMonth]);

  // Auto-refresh grid data every 5 minutes (snapshots arrive every 15 min)
  useEffect(() => {
    if (!isCurrentMonth) return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        const sp = standortId !== 'alle' ? `&standort_id=${standortId}` : '';
        api(`/api/forecast/grid?jahr=${year}&monat=${month}${sp}`).then(setGridData).catch(() => {});
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isCurrentMonth, year, month, standortId]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear((y) => y - 1); } else setMonth((m) => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear((y) => y + 1); } else setMonth((m) => m + 1); };

  return (
    <div className="p-6 lg:p-page">
      <PageHeader title={t('tagesplanung.title')} subtitle={t('tagesplanung.subtitle')} />

      {segmentWarning > 0 && (
        <div className="bg-warning-light/50 border border-amber-500/20 rounded-card p-3 flex items-start gap-3 mb-4">
          <div className="text-amber-400 flex-shrink-0 mt-0.5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-400">{t('forecast.segment_warning', { n: segmentWarning })}</div>
            <div className="text-xs text-content-tertiary mt-0.5">{t('forecast.segment_warning_detail')}</div>
          </div>
          <a href="/admin/segmente" className="text-xs text-amber-400 hover:text-amber-300 font-medium flex-shrink-0 mt-0.5">{t('forecast.segment_warning_link')} &rarr;</a>
        </div>
      )}

      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="xs" onClick={prevMonth} icon={ChevronLeft} className="text-content-tertiary" />
          <h2 className="text-lg font-bold min-w-[180px] text-center">{tMonth(month - 1)} {year}</h2>
          <Button variant="ghost" size="xs" onClick={nextMonth} icon={ChevronRight} className="text-content-tertiary" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={standortId} onChange={(e) => setStandortId(e.target.value)}
            className="bg-surface-card border border-line rounded-[10px] px-3 py-[6px] text-[13px] text-content focus:outline-none focus:border-forecast/40 cursor-pointer" style={{ fontFamily: 'inherit' }}>
            <option value="alle">{t('forecast.grid.all_locations')}</option>
            {standorte.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="inline-flex bg-surface-inset rounded-[10px] p-[3px] gap-[2px] border border-line">
            {[{ key: 'tag', labelKey: 'forecast.view.day' }, { key: 'woche', labelKey: 'forecast.view.week' }, { key: 'monat', labelKey: 'forecast.view.month' }].map((v) => (
              <button key={v.key} onClick={() => setViewMode(v.key)}
                className={`px-3 py-[5px] rounded-[7px] text-xs transition-colors ${viewMode === v.key ? 'bg-surface-card text-content font-semibold shadow-sm' : 'text-content-secondary hover:text-content'}`}>
                {t(v.labelKey)}
              </button>
            ))}
          </div>
          {viewMode === 'tag' && (
            <Button
              variant={showWeekends ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setShowWeekends(!showWeekends)}
              className={showWeekends ? 'bg-forecast/10 border-forecast/30 text-forecast' : 'text-[var(--text-secondary)]'}
            >
              {showWeekends ? t('forecast.mo_so') : t('forecast.mo_fr')}
            </Button>
          )}
          <div className="relative">
            <Button variant={moreMenuOpen || showSettings ? 'secondary' : 'ghost'} size="sm" onClick={() => setMoreMenuOpen(o => !o)} icon={MoreHorizontal} className={showSettings ? 'bg-forecast/10 border-forecast/30 text-forecast' : 'text-[var(--text-secondary)]'} aria-label={t('common.more', 'Mehr')} />
            {moreMenuOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setMoreMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-30 bg-surface-card border border-line rounded-[10px] shadow-sm py-1 min-w-[180px]">
                  <button
                    onClick={() => { setShowSettings(!showSettings); setMoreMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-content-secondary hover:text-content hover:bg-surface-hover transition-colors"
                  >
                    <Settings style={{ width: 14, height: 14 }} />
                    {t('modulSettings.title', 'Einstellungen')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showSettings ? (
        <ModulEinstellungen kategorien={['forecast_quality', 'berechnung']}
          filterKeys={['forecast_arbeitsende_uhr', 'forecast_rampup_min_orders', 'forecast_rampup_months', 'forecast_wochen_voraus', 'fte_effectivity_factor', 'fte_shift_duration_minutes',
            'fq_weight_accuracy', 'fq_weight_bias', 'fq_weight_consistency', 'fq_threshold_excellent', 'fq_threshold_good', 'fq_threshold_moderate',
            'fq_cutoff_hour', 'fq_min_days', 'fq_consistency_window', 'fq_consistency_multiplier', 'fq_warning_enabled', 'fq_warning_threshold',
            'fq_show_grid_badges', 'fq_badge_format', 'fq_ai_report_enabled', 'fq_ai_report_email', 'fq_ai_report_recipients']}
        />
      ) : (
      <>
      <KpiBar monatsData={monatsData} gridData={gridData} liveData={liveData} intradayData={intradayData} t={t} locale={locale} />
      <SegmentSummenBar segSummen={segSummen} t={t} locale={locale} />
      <AiSuggestionPanel year={year} month={month} monatsData={monatsData} onApplied={loadData} rampupMap={rampupMap} t={t} locale={locale} />
      <ForecastGrid gridData={gridData} monatsData={monatsData} year={year} month={month} viewMode={viewMode} setViewMode={setViewMode} liveData={liveData} intradayData={intradayData} showWeekends={showWeekends} weekendActivity={weekendActivity} rampupMap={rampupMap} fteBedarf={fteBedarf} demandMap={demandMap} aktionen={aktionen} feiertageGrid={feiertageGrid} t={t} locale={locale} tMonth={tMonth} tMonthShort={tMonthShort} />
      <ForecastWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onComplete={loadData} year={year} month={month} rampupMap={rampupMap} t={t} locale={locale} tMonth={tMonth} initialKundeId={initialParams.kundeId} initialYear={initialParams.jahr} initialMonth={initialParams.monat} />
      </>
      )}
    </div>
  );
}
